import React, { useState, useCallback, useEffect } from 'react';
import { 
	Box, 
	Drawer, 
	AppBar, 
	Toolbar, 
	Typography,
	IconButton,
	Button,
	TextField,
	MenuItem,
	List,
	ListItem,
	ListItemText,
	Divider,
	InputLabel,
	InputAdornment,
	Menu,
	MenuList,
	MenuItem as MuiMenuItem,
	Collapse,
	FormControlLabel,
	Switch,
	Select,
	FormControl
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import ImageIcon from '@mui/icons-material/Image';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import PreviewIcon from '@mui/icons-material/Preview';
import AddIcon from '@mui/icons-material/Add';
import { useGoogleLogin } from '@react-oauth/google';
import { getSheetData } from '../services/googleSheets';
import 'react-resizable/css/styles.css';
import LinkIcon from '@mui/icons-material/Link';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import VerticalAlignCenterIcon from '@mui/icons-material/VerticalAlignCenter';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import Slider from '@mui/material/Slider';
import ColorPicker from '@mui/material/colors';
import GradientIcon from '@mui/icons-material/Gradient';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import DeleteIcon from '@mui/icons-material/Delete';
import LayersIcon from '@mui/icons-material/Layers';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ShapeSelector from '../components/ShapeSelector';
import SVGElement from '../components/SVGElement';
import Draggable from 'react-draggable';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RemoveIcon from '@mui/icons-material/Remove';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Checkbox from '@mui/material/Checkbox';
import OutlinedInput from '@mui/material/OutlinedInput';

const DRAWER_WIDTH = 350;

// Add this array near the top of the file, after the DRAWER_WIDTH constant
const googleFonts = [
	'Roboto',
	'Open Sans',
	'Lato',
	'Montserrat',
	'Roboto Condensed',
	'Source Sans Pro',
	'Oswald',
	'Poppins',
	'Roboto Mono',
	'Raleway',
	'Noto Sans',
	'Ubuntu',
	'Playfair Display',
	'Merriweather',
	'PT Sans',
	'Lora',
	'Nunito',
	'Work Sans',
	'Fira Sans',
	'Quicksand'
];

// Add this helper function near the top of the component
const getMaskId = (element, bannerElements) => {
	if (!element.style?.maskLayerId) return null;	// Use maskLayerId instead of maskId
	return `${element.style?.maskLayerId}Mask`;
};

// Add this helper function to collect used fonts
const getUsedFonts = (banner) => {
	const usedFonts = new Set();
	
	banner.elements.forEach(element => {
		if (element.style?.fontFamily) {
			usedFonts.add(element.style.fontFamily);
		}
	});

	return Array.from(usedFonts);
};

// Add this constant at the top with your other constants
const maxFontSizes = [12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 56, 64, 72];

// Add this helper function for downloading files
const downloadFile = (content, fileName) => {
    const blob = new Blob([content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const BannerEditor = () => {
	// Keep these state declarations at the top with other states
	const [fillType, setFillType] = useState('solid');
	const [gradientColors, setGradientColors] = useState(['#000000', '#ffffff']);
	const [gradientAngle, setGradientAngle] = useState(90);

	const [bannerSizes, setBannerSizes] = useState([]);
	const [sheetUrl, setSheetUrl] = useState('');
	const [sheetData, setSheetData] = useState(null);
	const [addElementAnchor, setAddElementAnchor] = useState(null);
	const [selectedBannerIndex, setSelectedBannerIndex] = useState(null);
	const [showCellSelector, setShowCellSelector] = useState(false);
	const [selectedLanguage, setSelectedLanguage] = useState('');
	const [selectedCreative, setSelectedCreative] = useState(null);
	const [sheetRows, setSheetRows] = useState([]);
	const [selectedRow, setSelectedRow] = useState(null);
	const [accessToken, setAccessToken] = useState(null);

	const [currentMaskLayerId, setCurrentMaskLayerId] = useState(null);

	const [computedFontSizes, setComputedFontSizes] = useState({});

	const [bannerNames, setBannerNames] = useState({});  // Change to an object to store by dimensions
	const [editingBannerIndex, setEditingBannerIndex] = useState(null);

	const [selectedLibraries, setSelectedLibraries] = useState([]);

	const [customScriptUrl, setCustomScriptUrl] = useState('');

	const [isCustomUrlConfirmed, setIsCustomUrlConfirmed] = useState(false);

	// Animation settings
	const [selectedAnimationLibrary, setSelectedAnimationLibrary] = useState('');
	const [animationSettingsCollapsed, setAnimationSettingsCollapsed] = useState(true);
	const [includeScriptsCollapsed, setIncludeScriptsCollapsed] = useState(true);
	const [selectedClickTags, setSelectedClickTags] = useState([]);
	const [clickTagsCollapsed, setClickTagsCollapsed] = useState(true);
	const [animationSpeed, setAnimationSpeed] = useState('1.25');
	const [animationInterval, setAnimationInterval] = useState('0.2');
	const [animationDelay, setAnimationDelay] = useState('0.85');
	const [animationPause, setAnimationPause] = useState('3');
	const [animationBack, setAnimationBack] = useState('0.7');
	const [animationBackInout, setAnimationBackInout] = useState('0.7');
	const [animationTimeScale, setAnimationTimeScale] = useState('1.00');


	// Add this object to map library names to their URLs
	const libraryUrls = {
		GSAP: "https://s0.2mdn.net/ads/studio/cached_libs/gsap_3.11.5_min.js",
		jQuery: "https://ajax.googleapis.com/ajax/libs/jquery/3.7.1/jquery.min.js",
	};

	// Add this function to handle library selection
	const handleLibraryChange = (event) => {
		const value = event.target.value;
		setSelectedLibraries(
			// Remove 'none' if other options are selected, and remove other options if 'none' is selected
			value.includes('none') ? ['none'] : value.filter(v => v !== 'none')
		);
	};

	// Add this function to generate script tags for the HTML export
	const getSelectedLibraryScripts = () => {
		if (selectedLibraries.includes('none') || selectedLibraries.length === 0) {
			return '';
		}
		
		// Start with library scripts
		let scripts = selectedLibraries
			.filter(lib => libraryUrls[lib])
			.map(lib => `    <script src="${libraryUrls[lib]}"></script>`)
			.join('\n');
		
		// Add custom scripts if 'Custom' is selected
		if (selectedLibraries.includes('Custom')) {
			const customScriptTags = customScripts
				.filter(script => script.url.trim() && script.isConfirmed)
				.map(script => `    <script src="${script.url.trim()}"></script>`)
				.join('\n');
			
			if (customScriptTags) {
				scripts += '\n' + customScriptTags;
			}
		}
		
		return scripts;
	};

	const googleLogin = useGoogleLogin({
		onSuccess: async (tokenResponse) => {
			setAccessToken(tokenResponse.access_token);
		},
		scope: 'https://www.googleapis.com/auth/spreadsheets.readonly',
		flow: 'implicit',
		ux_mode: 'popup',
		redirect_uri: window.location.origin + '/editor'
	});

	const handleSheetConnect = async () => {
		try {
			if (!accessToken) {
				// Trigger Google login if not authenticated
				googleLogin();
				return;
			}

			const sheetId = extractSheetId(sheetUrl);
			if (!sheetId) {
				alert('Invalid Google Sheet URL');
				return;
			}

			// Fetch sheet data using our service
			const rows = await getSheetData(accessToken, sheetId);
			setSheetRows(rows);
			if (rows.length > 0) {
				setSelectedRow(rows[0]);
			}
		} catch (error) {
			console.error('Error connecting to sheet:', error);
			alert('Failed to connect to Google Sheet');
		}
	};

	const extractSheetId = (url) => {
		try {
			const regex = /\/d\/([a-zA-Z0-9-_]+)/;
			const match = url.match(regex);
			return match ? match[1] : null;
		} catch (error) {
			return null;
		}
	};

	const [elementMenu, setElementMenu] = useState(null);
	const [showTextSelector, setShowTextSelector] = useState(false);

	const handleAddElementClick = (event, bannerIndex) => {
		setSelectedBannerIndex(bannerIndex);
		setElementMenu(event.currentTarget);
	};

	const handleElementTypeSelect = (type) => {
		setElementMenu(null);
		
		if (selectedBannerIndex === null || !bannerSizes[selectedBannerIndex]) {
			alert('Please select a banner first');
			return;
		}

		if (type === 'text') {
			setShowTextSelector(true);
		} else if (type === 'button') {
			setShowButtonCellSelector(true);
		} else if (type === 'shape') {
			setShowShapeSelector(true);
		} else if (type === 'image') {
			setShowImageSelector(true);
		}
		// We'll handle image later
	};

	const handleCellSelect = (cellData) => {
		const newBannerSizes = [...bannerSizes];
		newBannerSizes[selectedBannerIndex].elements.push({
			id: Date.now(),
			type: 'text',
			content: cellData.value,
			position: { x: 0, y: 0 },
		});
		setBannerSizes(newBannerSizes);
		setShowCellSelector(false);
	};

	// Update the handleDrag function
	const handleDrag = (bannerId, elementId, e, data) => {
		const newBannerSizes = [...bannerSizes];
		const banner = newBannerSizes[bannerId];
		const draggedElement = banner.elements.find(el => el.id === elementId);

		// Calculate the delta from the original position
		const deltaX = data.x - draggedElement.position.x;
		const deltaY = data.y - draggedElement.position.y;

		// Update positions of all selected elements
		selectedElements.forEach(selectedId => {
			const element = banner.elements.find(el => el.id === selectedId);
			if (element) {
				element.position = {
					x: element.position.x + deltaX,
					y: element.position.y + deltaY
				};
			}
		});

		setBannerSizes(newBannerSizes);
	};

	// Update the left panel to show language selection and creative details
	const renderSheetDataPreview = () => {
		if (sheetRows.length === 0) return null;

		return (
			<Box sx={{ mt: 2 }}>
				<Typography variant="caption" color="textSecondary">
					Select Row
				</Typography>
				<TextField
					select
					fullWidth
					size="small"
					value={selectedRow?.rowId || ''}
					onChange={(e) => {
						const row = sheetRows.find(r => r.rowId === e.target.value);
						setSelectedRow(row);
					}}
					sx={{ mt: 1 }}
				>
					{sheetRows.map((row) => (
						<MenuItem key={row.rowId} value={row.rowId}>
							Row {row.rowId} - {row.language}
						</MenuItem>
					))}
				</TextField>

				{selectedRow && (
					<Box sx={{ mt: 2 }}>
						<Typography variant="caption" color="textSecondary">
							Row Content
						</Typography>
						<List dense sx={{ mt: 1 }}>
							{Object.entries(selectedRow.cells).map(([cell, value]) => (
								<ListItem 
									key={cell}
									sx={{ 
										bgcolor: 'background.paper',
										mb: 0.5,
										borderRadius: 1,
										border: '1px solid rgba(0,0,0,0.12)'
									}}
								>
									<ListItemText
										primary={value}
										secondary={`Cell ${cell}`}
										primaryTypographyProps={{
											variant: 'body2',
											fontWeight: 'medium'
										}}
										secondaryTypographyProps={{
											variant: 'caption'
										}}
									/>
									<Button 
										size="small"
										onClick={() => handleAddElement(selectedRow.rowId, cell, value)}
									>
										Use
									</Button>
								</ListItem>
							))}
						</List>
					</Box>
				)}
			</Box>
		);
	};

	// Update handleAddElement for text elements
	const handleAddElement = (rowId, cell, value) => {
		if (selectedBannerIndex === null) return;

		const newBannerSizes = [...bannerSizes];
		newBannerSizes[selectedBannerIndex].elements.push({
			id: Date.now(),
			layerId: `text_${cell}`,
			type: 'text',
			content: value,
			position: { x: 0, y: 0 },
			size: { width: 100, height: 50 },
			cell: cell,
			rowId: rowId,
			style: {	// Add style object for text elements
				fontFamily: 'Roboto',
				fontSize: '16px',
				textAlign: 'center',
				color: '#000000',
				// Add other text-related styles here
			}
		});
		setBannerSizes(newBannerSizes);
	};

	// Update the handleResize function with a simpler approach
	const handleResize = (bannerId, elementId, e, updates) => {
		const newBannerSizes = [...bannerSizes];
		const banner = newBannerSizes[bannerId];
		const elementIndex = banner.elements.findIndex(el => el.id === elementId);
		
		if (elementIndex !== -1) {
			const element = banner.elements[elementIndex];
			
			// Handle aspect ratio for images
			if (updates.size && element.type === 'image' && element.style?.keepAspectRatio !== false) {
				try {
					// Get original dimensions
					const originalWidth = element.size.width;
					const originalHeight = element.size.height;
					const aspectRatio = originalWidth / originalHeight;

					// Get the resize direction
					const direction = e.target.className.split('resize-handle-')[1];
					
					// Set minimum size
					const minSize = 20;
					let newWidth = Math.max(updates.size.width, minSize);
					let newHeight = Math.max(updates.size.height, minSize);

					// Handle resize based on which handle is being dragged
					if (direction.includes('e') || direction.includes('w')) {
						// Width is being changed directly
						newWidth = Math.max(updates.size.width, minSize);
						newHeight = newWidth / aspectRatio;
					} else if (direction.includes('n') || direction.includes('s')) {
						// Height is being changed directly
						newHeight = Math.max(updates.size.height, minSize);
						newWidth = newHeight * aspectRatio;
					} else {
						// Corner resize - use the larger change to determine the controlling dimension
						const widthChange = Math.abs(updates.size.width - originalWidth);
						const heightChange = Math.abs(updates.size.height - originalHeight);
						
						if (widthChange >= heightChange) {
							newWidth = Math.max(updates.size.width, minSize);
							newHeight = newWidth / aspectRatio;
						} else {
							newHeight = Math.max(updates.size.height, minSize);
							newWidth = newHeight * aspectRatio;
						}
					}

					// Update the size
					updates.size = {
						width: Math.round(newWidth),
						height: Math.round(newHeight)
					};

					// Adjust position for n, w, nw corners to maintain the opposite corner as anchor
					if (updates.position) {
						if (direction.includes('n')) {
							updates.position.y = element.position.y + (element.size.height - newHeight);
						}
						if (direction.includes('w')) {
							updates.position.x = element.position.x + (element.size.width - newWidth);
						}
					}
				} catch (error) {
					console.error('Error in resize calculation:', error);
					// If there's an error, just use the original updates
				}
			}

			// Apply the updates
			if (updates.size) {
				banner.elements[elementIndex].size = updates.size;
			}
			if (updates.position) {
				banner.elements[elementIndex].position = updates.position;
			}

			setBannerSizes(newBannerSizes);
		}
	};

	const [sheets, setSheets] = useState([]);
	const [selectedSheet, setSelectedSheet] = useState('');
	const [customSizes, setCustomSizes] = useState([]);
	const [showSizeInput, setShowSizeInput] = useState(false);
	const [newSize, setNewSize] = useState('');
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [sizeToDelete, setSizeToDelete] = useState(null);

	const handleAddSize = () => {
		if (!showSizeInput) {
			setShowSizeInput(true);
			return;
		}

		const sizeMatch = newSize.match(/(\d+)x(\d+)/);
		if (sizeMatch) {
			const width = parseInt(sizeMatch[1]);
			const height = parseInt(sizeMatch[2]);
			setCustomSizes([...customSizes, { width, height }]);
			setBannerSizes([...bannerSizes, { width, height, elements: [] }]);
			setNewSize('');
			setShowSizeInput(false);
		} else {
			alert('Please enter size in format: widthxheight (e.g., 300x600)');
		}
	};

	const handleDeleteClick = (index) => {
		setSizeToDelete(index);
		setDeleteConfirmOpen(true);
	};

	const handleDeleteConfirm = () => {
		if (sizeToDelete !== null) {
			const newCustomSizes = customSizes.filter((_, index) => index !== sizeToDelete);
			const newBannerSizes = bannerSizes.filter((_, index) => index !== sizeToDelete);
			setCustomSizes(newCustomSizes);
			setBannerSizes(newBannerSizes);
		}
		setDeleteConfirmOpen(false);
		setSizeToDelete(null);
	};

	// Filter and render available text cells
	const renderTextSelector = () => {
		if (!selectedRow || selectedBannerIndex === null || !bannerSizes[selectedBannerIndex]) return null;

		// Get all used cells in the current banner
		const usedCells = bannerSizes[selectedBannerIndex].elements
			.map(element => element.cell);

		const textCells = Object.entries(selectedRow.cells).filter(([cell, value]) => {
			// Filter out cells that have "Color" in their name or value
			// and cells that are already used
			return !cell.includes('Color') && 
						 !value.includes('Color') && 
						 !usedCells.includes(cell);
		});

		return (
			<Dialog 
				open={showTextSelector} 
				onClose={() => setShowTextSelector(false)}
				maxWidth="sm"
				fullWidth
				className="text-selector-dialog"
			>
				<DialogTitle>Select Text from Sheet</DialogTitle>
				<DialogContent>
					<List>
						{textCells.length > 0 ? (
							textCells.map(([cell, value]) => (
								<MuiMenuItem 
									key={cell}
									onClick={() => {
										handleAddElement(selectedRow.rowId, cell, value);
										setShowTextSelector(false);
									}}
								>
									<ListItemText 
										primary={cell}
										secondary={value}
										primaryTypographyProps={{
											style: {
												fontWeight: 500,
												fontSize: '16px'
											}
										}}
										secondaryTypographyProps={{
											style: {
												fontSize: '13px',
												color: 'rgba(0, 0, 0, 0.6)'
											}
										}}
									/>
								</MuiMenuItem>
							))
						) : (
							<Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
								All available text cells have been used
							</Typography>
						)}
					</List>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setShowTextSelector(false)}>Cancel</Button>
				</DialogActions>
			</Dialog>
		);
	};

	const [selectedElement, setSelectedElement] = useState(null);
	const [selectedFont, setSelectedFont] = useState('Roboto');
	const [selectedAlignment, setSelectedAlignment] = useState('center');
	
	// Change the selectedElements state from Set to Array
	const [selectedElements, setSelectedElements] = useState([]);

	// Update the handleElementClick function to maintain selection order
	const handleElementClick = (bannerId, elementId, e) => {
		if (e.shiftKey) {
			// Multi-select mode
			setSelectedElements(prev => {
				const elementIndex = prev.findIndex(id => id === elementId);
				if (elementIndex !== -1) {
					// Remove if already selected
					return prev.filter(id => id !== elementId);
				} else {
					// Add to selection while maintaining order
					return [...prev, elementId];
				}
			});
		} else {
			// Single select mode
			setSelectedElements([elementId]);
			const banner = bannerSizes[bannerId];
			const element = banner.elements.find(el => el.id === elementId);
			setSelectedElement({ ...element, bannerId });
			setSelectedFont(element.font || 'Roboto');
			setSelectedAlignment(element.alignment || 'center');
		}
	};

	// Update the handleFontChange function
	const handleFontChange = (font) => {
		if (!selectedElement) return;
		
		const newBannerSizes = [...bannerSizes];
		const banner = newBannerSizes[selectedElement.bannerId];
		const elementIndex = banner.elements.findIndex(el => el.id === selectedElement.id);
		
		if (elementIndex !== -1) {
			banner.elements[elementIndex].style = {
				...banner.elements[elementIndex].style,
				fontFamily: font
			};
			setBannerSizes(newBannerSizes);
			setSelectedElement({
				...selectedElement,
				style: {
					...selectedElement.style,
					fontFamily: font
				}
			});
		}
	};

	// Add this handler for alignment changes
	const handleAlignmentChange = (alignment) => {
		setSelectedAlignment(alignment);
		if (selectedElement) {
			const newBannerSizes = [...bannerSizes];
			const banner = newBannerSizes[selectedElement.bannerId];
			const elementIndex = banner.elements.findIndex(el => el.id === selectedElement.id);
			if (elementIndex !== -1) {
				banner.elements[elementIndex].alignment = alignment;
				setBannerSizes(newBannerSizes);
				setSelectedElement({
					...selectedElement,
					alignment: alignment
				});
			}
		}
	};

	// Update the handleMultiAlign function to align relative to first selected element
	const handleMultiAlign = (type) => {
		if (selectedElements.length < 2) return;

		const newBannerSizes = [...bannerSizes];
		const selectedBanner = newBannerSizes[selectedBannerIndex];
		
		// Get the first selected element as reference
		const firstElement = selectedBanner.elements.find(el => el.id === selectedElements[0]);
		if (!firstElement) return;

		// Get all selected items except the first one
		const itemsToAlign = selectedBanner.elements.filter(el => 
			selectedElements.slice(1).includes(el.id)
		);

		switch (type) {
			case 'left': {
				const referenceX = firstElement.position.x;
				itemsToAlign.forEach(element => {
					const elementIndex = selectedBanner.elements.findIndex(el => el.id === element.id);
					selectedBanner.elements[elementIndex].position.x = referenceX;
				});
				break;
			}
			case 'center': {
				const referenceCenterX = firstElement.position.x + firstElement.size.width/2;
				itemsToAlign.forEach(element => {
					const elementIndex = selectedBanner.elements.findIndex(el => el.id === element.id);
					selectedBanner.elements[elementIndex].position.x = referenceCenterX - element.size.width/2;
				});
				break;
			}
			case 'right': {
				const referenceRight = firstElement.position.x + firstElement.size.width;
				itemsToAlign.forEach(element => {
					const elementIndex = selectedBanner.elements.findIndex(el => el.id === element.id);
					selectedBanner.elements[elementIndex].position.x = referenceRight - element.size.width;
				});
				break;
			}
			case 'top': {
				const referenceY = firstElement.position.y;
				itemsToAlign.forEach(element => {
					const elementIndex = selectedBanner.elements.findIndex(el => el.id === element.id);
					selectedBanner.elements[elementIndex].position.y = referenceY;
				});
				break;
			}
			case 'middle': {
				const referenceCenterY = firstElement.position.y + firstElement.size.height/2;
				itemsToAlign.forEach(element => {
					const elementIndex = selectedBanner.elements.findIndex(el => el.id === element.id);
					selectedBanner.elements[elementIndex].position.y = referenceCenterY - element.size.height/2;
				});
				break;
			}
			case 'bottom': {
				const referenceBottom = firstElement.position.y + firstElement.size.height;
				itemsToAlign.forEach(element => {
					const elementIndex = selectedBanner.elements.findIndex(el => el.id === element.id);
					selectedBanner.elements[elementIndex].position.y = referenceBottom - element.size.height;
				});
				break;
			}
		}

		setBannerSizes(newBannerSizes);
	};

	// Add state for both types of spacing
	const [horizontalSpacing, setHorizontalSpacing] = useState(0);
	const [verticalSpacing, setVerticalSpacing] = useState(0);

	// Update the spacing handler to handle both directions
	const handleSpacing = (value, direction) => {
		if (selectedElements.length < 2) return;

		const newBannerSizes = [...bannerSizes];
		const selectedBanner = newBannerSizes[selectedBannerIndex];
		const selectedItems = selectedBanner.elements.filter(el => selectedElements.includes(el.id));

		if (direction === 'horizontal') {
			// Sort by x position
			const sortedItems = [...selectedItems].sort((a, b) => a.position.x - b.position.x);
			
			// Start with the leftmost element's position
			let prevElement = sortedItems[0];
			
			// Adjust positions maintaining the specified gap between elements
			for (let i = 1; i < sortedItems.length; i++) {
				const elementIndex = selectedBanner.elements.findIndex(el => el.id === sortedItems[i].id);
				const newX = prevElement.position.x + prevElement.size.width + value;
				selectedBanner.elements[elementIndex].position.x = newX;
				prevElement = selectedBanner.elements[elementIndex];
			}
		} else {
			// Sort by y position
			const sortedItems = [...selectedItems].sort((a, b) => a.position.y - b.position.y);
			
			// Start with the topmost element's position
			let prevElement = sortedItems[0];
			
			// Adjust positions maintaining the specified gap between elements
			for (let i = 1; i < sortedItems.length; i++) {
				const elementIndex = selectedBanner.elements.findIndex(el => el.id === sortedItems[i].id);
				const newY = prevElement.position.y + prevElement.size.height + value;
				selectedBanner.elements[elementIndex].position.y = newY;
				prevElement = selectedBanner.elements[elementIndex];
			}
		}

		setBannerSizes(newBannerSizes);
	};

	// Update the AlignmentTools component to include both spacing controls
	const AlignmentTools = () => {
		if (selectedElements.length < 2) return null;

		return (
			<Box
				className="alignment-tools"
				sx={{
					position: 'fixed',
					top: '80px',
					right: '20px',
					backgroundColor: 'white',
					borderRadius: '4px',
					boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
					padding: '16px',
					zIndex: 1000,
				}}
			>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
					{/* Horizontal Alignment */}
					<Box>
						<Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
							Horizontal Alignment
						</Typography>
						<Box sx={{ display: 'flex', gap: 1 }}>
							<IconButton size="small" onClick={() => handleMultiAlign('left')}>
								<VerticalAlignTopIcon sx={{ transform: 'rotate(-90deg)' }} />
							</IconButton>
							<IconButton size="small" onClick={() => handleMultiAlign('center')}>
								<VerticalAlignCenterIcon sx={{ transform: 'rotate(90deg)' }} />
							</IconButton>
							<IconButton size="small" onClick={() => handleMultiAlign('right')}>
								<VerticalAlignBottomIcon sx={{ transform: 'rotate(-90deg)' }} />
							</IconButton>
						</Box>
					</Box>

					{/* Vertical Alignment */}
					<Box>
						<Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
							Vertical Alignment
						</Typography>
						<Box sx={{ display: 'flex', gap: 1 }}>
							<IconButton size="small" onClick={() => handleMultiAlign('top')}>
								<VerticalAlignTopIcon />
							</IconButton>
							<IconButton size="small" onClick={() => handleMultiAlign('middle')}>
								<VerticalAlignCenterIcon />
							</IconButton>
							<IconButton size="small" onClick={() => handleMultiAlign('bottom')}>
								<VerticalAlignBottomIcon />
							</IconButton>
						</Box>
					</Box>

					{/* Horizontal Spacing Controls */}
					<Box>
						<Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
							Horizontal Spacing
						</Typography>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
							<Slider
								size="small"
								value={horizontalSpacing}
								onChange={(e, value) => {
									setHorizontalSpacing(value);
									handleSpacing(value, 'horizontal');
								}}
								min={0}
								max={50}
								valueLabelDisplay="auto"
								sx={{ width: '100px' }}
							/>
							<TextField
								size="small"
								value={horizontalSpacing}
								onChange={(e) => {
									const value = parseInt(e.target.value) || 0;
									setHorizontalSpacing(value);
									handleSpacing(value, 'horizontal');
								}}
								inputProps={{
									type: 'number',
									min: 0,
									max: 50,
									style: { width: '50px' }
								}}
							/>
							<Typography variant="caption">px</Typography>
						</Box>
					</Box>

					{/* Vertical Spacing Controls */}
					<Box>
						<Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
							Vertical Spacing
						</Typography>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
							<Slider
								size="small"
								value={verticalSpacing}
								onChange={(e, value) => {
									setVerticalSpacing(value);
									handleSpacing(value, 'vertical');
								}}
								min={0}
								max={50}
								valueLabelDisplay="auto"
								sx={{ width: '100px' }}
							/>
							<TextField
								size="small"
								value={verticalSpacing}
								onChange={(e) => {
									const value = parseInt(e.target.value) || 0;
									setVerticalSpacing(value);
									handleSpacing(value, 'vertical');
								}}
								inputProps={{
									type: 'number',
									min: 0,
									max: 50,
									style: { width: '50px' }
								}}
							/>
							<Typography variant="caption">px</Typography>
						</Box>
					</Box>
				</Box>
			</Box>
		);
	};

	// Update the calculateFontSize function to be more precise
	const calculateFontSize = (text, width, height, fontFamily, padding) => {
		const canvas = document.createElement('canvas');
		const ctx = canvas.getContext('2d');
		
		// Calculate available space accounting for padding
		const availableWidth = width - (padding?.left || 0) - (padding?.right || 0);
		const availableHeight = height - (padding?.top || 0) - (padding?.bottom || 0);
		
		// Binary search for the optimal font size
		let minSize = 1;
		let maxSize = Math.min(availableWidth, availableHeight);
		let optimalSize = minSize;

		while (minSize <= maxSize) {
			const mid = Math.floor((minSize + maxSize) / 2);
			ctx.font = `${mid}px ${fontFamily}`;
			const metrics = ctx.measureText(text);
			const textWidth = metrics.width;
			const textHeight = mid; // Approximate height based on font size

			if (textWidth <= availableWidth && textHeight <= availableHeight) {
				optimalSize = mid;
				minSize = mid + 1;
			} else {
				maxSize = mid - 1;
			}
		}

		return optimalSize * 0.9; // 90% of the maximum size for some padding
	};

	// Separate render functions for different element types
	const renderTextElement = (element, bannerId) => {
		return (
			<Draggable
				key={element.id}
				position={element.position}
				onDrag={(e, data) => handleDrag(bannerId, element.id, e, data)}
				handle=".drag-handle"
				onMouseDown={(e) => {
					handleElementClick(bannerId, element.id, e);
					e.stopPropagation();
				}}
			>
				<div 
					id={element.layerId}
					className={`txt_constraint ${selectedElement?.id === element.id ? 'selected' : ''} ${selectedElements.includes(element.id) ? 'multi-selected' : ''}`}
					style={{
						width: element.size.width,
						height: element.size.height,
					}}
				>
					<div className="drag-handle">
						<div className="txt_container">
							{element.type === 'text' && (() => {
								// Get the actual rendered font size from the element's style
								const txtElement = document.querySelector(`#${element.layerId} .txt`);
								const computedStyle = txtElement ? window.getComputedStyle(txtElement) : null;
								const actualFontSize = computedStyle?.fontSize || '16px';

								// Store the computed font size in state
								if (computedStyle?.fontSize) {
									setComputedFontSizes(prev => ({
										...prev,
										[element.layerId]: actualFontSize
									}));
								}

								return (
									<div className="txt_container">
										<div 
											className="txt"
											style={{
												fontFamily: element.style?.fontFamily || 'Roboto',
												fontSize: actualFontSize,
												textAlign: element.style?.textAlign || 'center',
												color: element.style?.color || '#000000',
												textTransform: element.style?.textTransform || 'none',
												fontWeight: element.style?.fontWeight || 'normal',
												fontStyle: element.style?.fontStyle || 'normal',
												lineHeight: element.style?.lineHeight || '1',
												width: '100%',
												height: '100%',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
											}}
										>
											{element.content}
										</div>
									</div>
								);
							})()}
						</div>
					</div>

					{/* Delete button */}
					<div 
						className="element-delete"
						onClick={(e) => {
							e.stopPropagation();
							handleDeleteElement(element.id);
						}}
					>
						<CloseIcon sx={{ fontSize: 12 }} />
					</div>

					{/* Resize handles */}
					{selectedElement?.id === element.id && (
						<>
							<div className="resize-handle resize-handle-n" onMouseDown={(e) => handleResizeStart(e, 'n')} />
							<div className="resize-handle resize-handle-s" onMouseDown={(e) => handleResizeStart(e, 's')} />
							<div className="resize-handle resize-handle-e" onMouseDown={(e) => handleResizeStart(e, 'e')} />
							<div className="resize-handle resize-handle-w" onMouseDown={(e) => handleResizeStart(e, 'w')} />
							<div className="resize-handle resize-handle-nw" onMouseDown={(e) => handleResizeStart(e, 'nw')} />
							<div className="resize-handle resize-handle-ne" onMouseDown={(e) => handleResizeStart(e, 'ne')} />
							<div className="resize-handle resize-handle-sw" onMouseDown={(e) => handleResizeStart(e, 'sw')} />
							<div className="resize-handle resize-handle-se" onMouseDown={(e) => handleResizeStart(e, 'se')} />
						</>
					)}
				</div>
			</Draggable>
		);
	};

	const renderButtonElement = (element, bannerId) => {
		return (
			<Draggable
				key={element.id}
				position={element.position}
				onDrag={(e, data) => handleDrag(bannerId, element.id, e, data)}
				handle=".drag-handle"
				onMouseDown={(e) => {
					handleElementClick(bannerId, element.id, e);
					e.stopPropagation();
				}}
			>
				<div 
					id={element.layerId}
					className={`txt_constraint ${selectedElement?.id === element.id ? 'selected' : ''} ${selectedElements.includes(element.id) ? 'multi-selected' : ''}`}
					style={{
						width: element.size.width,
						height: element.size.height,
						position: 'absolute',
					}}
				>
					<div className="drag-handle">
						<div className="txt_container">
							<div 
								className="txt"
								style={{
									width: '100%',
									height: '100%',
									background: element.style?.backgroundColor || '#ffffff',
									borderRadius: `${element.style?.borderRadius || 0}px`,
									border: `${element.style?.borderWidth || 0}px ${element.style?.borderStyle || 'solid'} ${element.style?.borderColor || '#000000'}`,
								}}>
								<div 
									className="txt"
									style={{
										width: '100%',
										height: '100%',
										color: element.style?.color || '#000000',
										fontSize: element.style?.fontSize || '16px',
										fontFamily: element.style?.fontFamily || 'Roboto',
										fontWeight: element.style?.fontWeight || 'normal',	// Add fontWeight
										fontStyle: element.style?.fontStyle || 'normal',		// Add fontStyle
										padding: `${element.style?.padding?.top || 0}px ${element.style?.padding?.right || 0}px ${element.style?.padding?.bottom || 0}px ${element.style?.padding?.left || 0}px`,
										display: 'flex',
										alignItems: element.style?.padding?.top === element.style?.padding?.bottom ? 'center' : 'flex-start',
										justifyContent: 'flex-start',
										textAlign: 'left',
										lineHeight: '1',
										whiteSpace: 'nowrap',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										boxSizing: 'border-box'
									}}
								>
									{element.content}
								</div>
							</div>
						</div>
					</div>

					{/* Delete button */}
					<div 
						className="element-delete"
						onClick={(e) => {
							e.stopPropagation();
							handleDeleteElement(element.id);
						}}
					>
						<CloseIcon sx={{ fontSize: 12 }} />
					</div>

					{/* Resize handles - same as text fields */}
					{selectedElement?.id === element.id && (
						<>
							<div className="resize-handle resize-handle-n" onMouseDown={(e) => handleResizeStart(e, 'n')} />
							<div className="resize-handle resize-handle-s" onMouseDown={(e) => handleResizeStart(e, 's')} />
							<div className="resize-handle resize-handle-e" onMouseDown={(e) => handleResizeStart(e, 'e')} />
							<div className="resize-handle resize-handle-w" onMouseDown={(e) => handleResizeStart(e, 'w')} />
							<div className="resize-handle resize-handle-nw" onMouseDown={(e) => handleResizeStart(e, 'nw')} />
							<div className="resize-handle resize-handle-ne" onMouseDown={(e) => handleResizeStart(e, 'ne')} />
							<div className="resize-handle resize-handle-sw" onMouseDown={(e) => handleResizeStart(e, 'sw')} />
							<div className="resize-handle resize-handle-se" onMouseDown={(e) => handleResizeStart(e, 'se')} />
						</>
					)}
				</div>
			</Draggable>
		);
	};

	// Update the renderShapeElement function
	const renderShapeElement = (element, bannerId) => {
		return (
			<SVGElement
				key={element.id}
				element={element}	// This now includes the zIndex
				selected={selectedElement?.id === element.id}
				multiSelected={selectedElements.includes(element.id)}
				onDrag={(e, position) => handleSVGDrag(bannerId, element.id, e, position)}
				onResize={(e, updates) => handleSVGResize(bannerId, element.id, e, updates)}
				onClick={(e) => {
					handleElementClick(bannerId, element.id, e);
					e.stopPropagation();
				}}
				onDelete={() => handleDeleteElement(element.id)}
				bannerSizes={bannerSizes}
				selectedBannerIndex={selectedBannerIndex}
			/>
		);
	};

	// Update the main renderElement function to use the specific renderers
	const renderElement = (element, bannerId) => {
		return (
			<React.Fragment key={element.id}>
				{/* Actual Content Layer */}
				<div 
					id={element.layerId}
					className="banner-element"
					style={{
						position: 'absolute',
						left: element.position.x,
						top: element.position.y,
						width: element.size.width,
						height: element.size.height,
						pointerEvents: 'none',
						zIndex: 1
					}}
				>
					{element.type === 'text' && (
						<div className="txt_container">
							<div 
								className="txt"
								style={{
									fontFamily: element.style?.fontFamily || 'Roboto',
									fontSize: `${element.size.height * 0.7}px`,	// THIS is the actual calculation!
									textAlign: element.style?.textAlign || 'center',
									color: element.style?.color || '#000000',
									textTransform: element.style?.textTransform || 'none',
									fontWeight: element.style?.fontWeight || 'normal',
									fontStyle: element.style?.fontStyle || 'normal',
									lineHeight: element.style?.lineHeight || '1',
									width: '100%',
									height: '100%',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
							>
								{element.content}
							</div>
						</div>
					)}

					{element.type === 'button' && (
						<div className="txt_container" style={{
							width: '100%',
							height: '100%',
							backgroundColor: element.style?.backgroundColor || '#ffffff',
							borderRadius: `${element.style?.borderRadius || 0}px`,
							border: `${element.style?.borderWidth || 0}px ${element.style?.borderStyle || 'solid'} ${element.style?.borderColor || '#000000'}`,
						}}>
							<div 
								className="txt"
								style={{
									width: '100%',
									height: '100%',
									color: element.style?.color || '#000000',
									fontSize: element.style?.fontSize || '16px',
									fontFamily: element.style?.fontFamily || 'Roboto',
									fontWeight: element.style?.fontWeight || 'normal',	// Add fontWeight
									fontStyle: element.style?.fontStyle || 'normal',		// Add fontStyle
									padding: `${element.style?.padding?.top || 0}px ${element.style?.padding?.right || 0}px ${element.style?.padding?.bottom || 0}px ${element.style?.padding?.left || 0}px`,
									display: 'flex',
									alignItems: element.style?.padding?.top === element.style?.padding?.bottom ? 'center' : 'flex-start',
									justifyContent: 'flex-start',
									textAlign: 'left',
									lineHeight: '1',
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									boxSizing: 'border-box'
								}}
							>
								{element.content}
							</div>
						</div>
					)}

					{element.type === 'shape' && (
						<SVGElement
							element={element}
							style={{
								width: '100%',
								height: '100%',
								fill: element.style?.fill || '#000000',
								stroke: element.style?.stroke || 'none',
								strokeWidth: element.style?.strokeWidth || 0,
								opacity: element.style?.opacity || 1,
								transform: `rotate(${element.style?.rotation || 0}deg)`,
								mixBlendMode: element.style?.mixBlendMode || 'normal'
							}}
							bannerSizes={bannerSizes}
							selectedBannerIndex={selectedBannerIndex}
						/>
					)}

					{element.type === 'image' && (() => {
						// Find the shape being used as mask
						const maskShape = element.style?.maskId ? 
							bannerSizes[selectedBannerIndex].elements.find(el => 
								el.id === element.style.maskId
							) : null;

						// Get the layerId from the shape
						const maskShapeLayerId = maskShape?.layerId;

						return (
							<img 
								src={element.content} 
								alt={element.layerId || ''}
								style={{
									width: `${element.size.width}px`,	// Use exact width
									height: `${element.size.height}px`, // Use exact height
									objectFit: element.style?.objectFit || 'contain',
									opacity: element.style?.opacity || 1,
									mixBlendMode: element.style?.mixBlendMode || 'normal',
									// Use maskShape.layerId directly
									mask: maskShapeLayerId ? `url(#${maskShapeLayerId}Mask)` : 'none',
									WebkitMask: maskShapeLayerId ? `url(#${maskShapeLayerId}Mask)` : 'none'
								}}
							/>
						);
					})()}
				</div>

				{/* UI Controls Layer */}
				<Draggable
					position={element.position}
					onDrag={(e, data) => handleDrag(bannerId, element.id, e, data)}
					handle=".drag-handle"
					onMouseDown={(e) => {
						handleElementClick(bannerId, element.id, e);
						e.stopPropagation();
					}}
				>
					<div 
						className={`element-controls ${selectedElement?.id === element.id ? 'selected' : ''} ${selectedElements.includes(element.id) ? 'multi-selected' : ''}`}
						style={{
							position: 'absolute',
							width: element.size.width,
							height: element.size.height,
							pointerEvents: 'auto',
							zIndex: 2
						}}
					>
						{/* Transparent drag handle */}
						<div className="drag-handle" style={{ width: '100%', height: '100%' }} />

						{/* Delete button */}
						<div 
							className="element-delete"
							onClick={(e) => {
								e.stopPropagation();
								handleDeleteElement(element.id);
							}}
						>
							<CloseIcon sx={{ fontSize: 12 }} />
						</div>

						{/* Add Aspect Ratio Toggle for images */}
						{element.type === 'image' && (
							<div 
								className="aspect-ratio-toggle"
								onClick={(e) => {
									e.stopPropagation();
									const keepAspectRatio = element.style?.keepAspectRatio !== false;
									const updatedElement = {
										...element,
										style: {
											...element.style,
											keepAspectRatio: !keepAspectRatio
										}
									};
									handleElementUpdate(updatedElement);
								}}
							>
								{element.style?.keepAspectRatio !== false ? <LinkIcon /> : <LinkOffIcon />}
							</div>
						)}

						{/* Resize handles */}
						{selectedElement?.id === element.id && (
							<>
								<div className="resize-handle resize-handle-n" onMouseDown={(e) => handleResizeStart(e, 'n')} />
								<div className="resize-handle resize-handle-s" onMouseDown={(e) => handleResizeStart(e, 's')} />
								<div className="resize-handle resize-handle-e" onMouseDown={(e) => handleResizeStart(e, 'e')} />
								<div className="resize-handle resize-handle-w" onMouseDown={(e) => handleResizeStart(e, 'w')} />
								<div className="resize-handle resize-handle-nw" onMouseDown={(e) => handleResizeStart(e, 'nw')} />
								<div className="resize-handle resize-handle-ne" onMouseDown={(e) => handleResizeStart(e, 'ne')} />
								<div className="resize-handle resize-handle-sw" onMouseDown={(e) => handleResizeStart(e, 'sw')} />
								<div className="resize-handle resize-handle-se" onMouseDown={(e) => handleResizeStart(e, 'se')} />
							</>
						)}
					</div>
				</Draggable>
			</React.Fragment>
		);
	};

	const [resizeRef, setResizeRef] = useState(null);

	// Update the resize handlers
	const handleResizeStart = useCallback((e, direction) => {
		e.preventDefault();
		e.stopPropagation();

		if (!selectedElement) return;

		const element = bannerSizes[selectedElement.bannerId].elements.find(
			el => el.id === selectedElement.id
		);

		if (!element) return;

		const startX = e.clientX;
		const startY = e.clientY;
		const startWidth = element.size.width;
		const startHeight = element.size.height;
		const startPosition = { ...element.position };

		const handleMouseMove = (moveEvent) => {
			moveEvent.preventDefault();
			moveEvent.stopPropagation();

			const deltaX = moveEvent.clientX - startX;
			const deltaY = moveEvent.clientY - startY;

			const newBannerSizes = [...bannerSizes];
			const banner = newBannerSizes[selectedElement.bannerId];
			const elementToResize = banner.elements.find(el => el.id === element.id);

			let newWidth = startWidth;
			let newHeight = startHeight;
			let newX = startPosition.x;
			let newY = startPosition.y;

			switch (direction) {
				case 'e':
					newWidth = Math.max(50, startWidth + deltaX);
					break;
				case 'w':
					newWidth = Math.max(50, startWidth - deltaX);
					newX = startPosition.x + deltaX;
					break;
				case 's':
					newHeight = Math.max(30, startHeight + deltaY);
					break;
				case 'n':
					newHeight = Math.max(30, startHeight - deltaY);
					newY = startPosition.y + deltaY;
					break;
				case 'se':
					newWidth = Math.max(50, startWidth + deltaX);
					newHeight = Math.max(30, startHeight + deltaY);
					break;
				case 'sw':
					newWidth = Math.max(50, startWidth - deltaX);
					newHeight = Math.max(30, startHeight + deltaY);
					newX = startPosition.x + deltaX;
					break;
				case 'ne':
					newWidth = Math.max(50, startWidth + deltaX);
					newHeight = Math.max(30, startHeight - deltaY);
					newY = startPosition.y + deltaY;
					break;
				case 'nw':
					newWidth = Math.max(50, startWidth - deltaX);
					newHeight = Math.max(30, startHeight - deltaY);
					newX = startPosition.x + deltaX;
					newY = startPosition.y + deltaY;
					break;
			}

			elementToResize.size = { width: newWidth, height: newHeight };
			elementToResize.position = { x: newX, y: newY };

			setBannerSizes([...newBannerSizes]);
		};

		const handleMouseUp = () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);
	}, [selectedElement, bannerSizes]);

	// Add the handleElementDelete function
	const handleElementDelete = (bannerId, elementId) => {
		const newBannerSizes = [...bannerSizes];
		const banner = newBannerSizes[bannerId];
		banner.elements = banner.elements.filter(el => el.id !== elementId);
		setBannerSizes(newBannerSizes);
		if (selectedElement?.id === elementId) {
			setSelectedElement(null);
			setSelectedFont('Roboto');
		}
	};

	// Add this effect to handle text scaling
	React.useEffect(() => {
		const scaleText = () => {
			document.querySelectorAll('.txt_container').forEach(container => {
				const textElement = container.querySelector('.txt');
				if (!textElement) return;

				// Start with a large font size
				let fontSize = 100;
				textElement.style.fontSize = `${fontSize}px`;

				// Binary search for the best font size
				let min = 1;
				const elementId = textElement.closest('[id]').id;
				const element = bannerSizes[selectedBannerIndex].elements.find(el => el.layerId === elementId);
				let max = element?.maxFontSize || 16;
				
				while (min <= max) {
					fontSize = Math.floor((min + max) / 2);
					textElement.style.fontSize = `${fontSize}px`;

					if (textElement.scrollHeight <= container.clientHeight && 
							textElement.scrollWidth <= container.clientWidth) {
						min = fontSize + 1;
					} else {
						max = fontSize - 1;
					}
				}

				// Set the final font size slightly smaller to ensure fit
				textElement.style.fontSize = `${max}px`;
			});
		};

		scaleText();
		
		const resizeObserver = new ResizeObserver(scaleText);
		document.querySelectorAll('.txt_container').forEach(container => {
			resizeObserver.observe(container);
		});

		return () => {
			resizeObserver.disconnect();
		};
	}, [bannerSizes, selectedElement, selectedBannerIndex]);

	const [gradientStops, setGradientStops] = useState([
		{ position: 0, color: '#31cb4b' },
		{ position: 100, color: '#2cb543' }
	]);
	const [gradientType, setGradientType] = useState('solid'); // 'solid', 'linear', 'radial'
	const [borderRadius, setBorderRadius] = useState(0);
	const [borderWidth, setBorderWidth] = useState(0);
	const [borderStyle, setBorderStyle] = useState('solid');
	const [borderColor, setBorderColor] = useState('#2cb543');
	const [hoverEnabled, setHoverEnabled] = useState(false);

	// Add font state to the existing button states
	const [buttonFont, setButtonFont] = useState('Roboto');

	// Add this state for button padding
	const [buttonPadding, setButtonPadding] = useState({
		top: 4,
		right: 4,
		bottom: 4,
		left: 4
	});

	// Update the handleButtonStyleChange function to properly handle padding updates
	const handleButtonStyleChange = (property, value) => {
		if (!selectedElement) return;

		const newBannerSizes = [...bannerSizes];
		const banner = newBannerSizes[selectedBannerIndex];
		const elementIndex = banner.elements.findIndex(el => el.id === selectedElement.id);

		if (elementIndex === -1) return;

		const updatedElement = { ...banner.elements[elementIndex] };

		if (property === 'padding') {
			updatedElement.style = {
				...updatedElement.style,
				padding: {
					...updatedElement.style?.padding,
					...value
				}
			};
		} else if (property === 'backgroundColor' && value.includes('gradient')) {
			// Handle gradient backgrounds
			updatedElement.style = {
				...updatedElement.style,
				backgroundColor: value,
				fillType: fillType
			};
		} else {
			// Handle all other style properties
			updatedElement.style = {
				...updatedElement.style,
				[property]: value
			};
		}

		banner.elements[elementIndex] = updatedElement;
		setBannerSizes(newBannerSizes);
	};

	const handleGradientStopChange = (index, field, value) => {
		const newStops = [...gradientStops];
		newStops[index][field] = value;
		if (field === 'position') {
			newStops.sort((a, b) => a.position - b.position);
		}
		setGradientStops(newStops);

		// Apply the gradient to the button
		if (selectedElement) {
			handleButtonStyleChange('backgroundColor', getGradientString());
		}
	};

	const handleAddGradientStop = () => {
		const newStop = {
			position: 50,
			color: gradientStops[0].color
		};
		setGradientStops([...gradientStops, newStop].sort((a, b) => a.position - b.position));
	};

	const getGradientString = () => {
		if (gradientType === 'solid') return gradientStops[0].color;
		
		const stops = gradientStops
			.map(stop => `${stop.color} ${stop.position}%`)
			.join(', ');
			
		if (gradientType === 'linear') {
			return `linear-gradient(${gradientAngle}deg, ${stops})`;
		}
		return `radial-gradient(circle, ${stops})`;
	};

	// Add new state for linked padding
	const [linkedPadding, setLinkedPadding] = useState({
		vertical: false,	// for top/bottom
		horizontal: false // for left/right
	});

	// Update the padding controls in ButtonSettings component
	const ButtonSettings = () => {
		if (!selectedElement?.type === 'button') return null;

		return (
			<Box sx={{ mt: 2 }}>

				{/* Font Family Dropdown */}
				<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
					Font Family
				</InputLabel>
				<Select
					value={selectedElement.style?.fontFamily || 'Roboto'}
					onChange={(e) => handleButtonStyleChange('fontFamily', e.target.value)}
					size="small"
					fullWidth
					sx={{ bgcolor: 'white', mb: 2 }}
				>
					{googleFonts.map(font => (
						<MenuItem 
							key={font} 
							value={font}
							sx={{ fontFamily: font }}
						>
							{font}
						</MenuItem>
					))}
				</Select>

				<Box sx={{ mb: 2 }}>
					<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
						Max Font Size
					</InputLabel>
					<FormControl fullWidth sx={{ bgcolor: 'white', mb: 1 }}>
						<Select
							size="small"
							value={selectedElement.maxFontSize || 16}
							onChange={(e) => {
								handleElementUpdate({
									...selectedElement,
									maxFontSize: e.target.value
								});
							}}
						>
							{maxFontSizes.map((size) => (
								<MenuItem key={size} value={size}>
									{size}px
								</MenuItem>
							))}
						</Select>
					</FormControl>
					
					<InputLabel sx={{ color: 'white', fontSize: '12px', mt: 1 }}>
						Current Font Size
					</InputLabel>
					<TextField
						fullWidth
						value={(() => {
							const textElement = document.querySelector(`#${selectedElement.layerId} .txt`);
							return textElement ? `${parseInt(window.getComputedStyle(textElement).fontSize)}px` : '0px';
						})()}
						InputProps={{
							readOnly: true,
						}}
						variant="outlined"
						size="small"
						sx={{ mt: 1 }}
					/>
				</Box>

				
				{/* Font Color */}
				<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
					Font Color
				</InputLabel>
				<input
					type="color"
					value={selectedElement.style?.color || '#000000'}
					onChange={(e) => handleButtonStyleChange('color', e.target.value)}
					style={{ width: '100%', height: '40px', marginBottom: '16px' }}
				/>

				{/* Text Formatting Controls */}
				<Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
					{/* Bold */}
					<IconButton
						onClick={() => handleButtonStyleChange('fontWeight', 
							selectedElement.style?.fontWeight === 'bold' ? 'normal' : 'bold'
						)}
						sx={{ 
							flex: 1,
							color: selectedElement.style?.fontWeight === 'bold' ? 'var(--orange)' : 'white',
							border: `1px solid ${selectedElement.style?.fontWeight === 'bold' ? 'var(--orange)' : 'rgba(255,255,255,0.23)'}`,
							padding: '8px'
						}}
					>
						<FormatBoldIcon />
					</IconButton>

					{/* Italic */}
					<IconButton
						onClick={() => handleButtonStyleChange('fontStyle',
							selectedElement.style?.fontStyle === 'italic' ? 'normal' : 'italic'
						)}
						sx={{ 
							flex: 1,
							color: selectedElement.style?.fontStyle === 'italic' ? 'var(--orange)' : 'white',
							border: `1px solid ${selectedElement.style?.fontStyle === 'italic' ? 'var(--orange)' : 'rgba(255,255,255,0.23)'}`,
							padding: '8px'
						}}
					>
						<FormatItalicIcon />
					</IconButton>

					{/* Text Transform */}
					<Select
						value={selectedElement.style?.textTransform || 'none'}
						onChange={(e) => handleButtonStyleChange('textTransform', e.target.value)}
						size="small"
						sx={{ 
							flex: 2,
							bgcolor: 'white'
						}}
					>
						<MenuItem value="none">Normal Case</MenuItem>
						<MenuItem value="uppercase">UPPERCASE</MenuItem>
						<MenuItem value="lowercase">lowercase</MenuItem>
						<MenuItem value="capitalize">Capitalize</MenuItem>
					</Select>
				</Box>

				<Box sx={{ mb: 2 }}>
					<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
						Text Alignment
					</InputLabel>
					<Box sx={{ display: 'flex', gap: 1 }}>
						<IconButton 
							onClick={() => handleElementUpdate({
								...selectedElement,
								style: {
									...selectedElement.style,
									textAlign: 'left',
									justifyContent: 'flex-start'
								}
							})}
							sx={{ 
								bgcolor: selectedElement.style?.textAlign === 'left' ? 'rgba(238, 75, 15, 0.1)' : 'transparent',
								color: selectedElement.style?.textAlign === 'left' ? 'var(--orange)' : 'inherit'
							}}
						>
							<FormatAlignLeftIcon />
						</IconButton>
						
						<IconButton 
							onClick={() => handleElementUpdate({
								...selectedElement,
								style: {
									...selectedElement.style,
									textAlign: 'center',
									justifyContent: 'center'
								}
							})}
							sx={{ 
								bgcolor: selectedElement.style?.textAlign === 'center' ? 'rgba(238, 75, 15, 0.1)' : 'transparent',
								color: selectedElement.style?.textAlign === 'center' ? 'var(--orange)' : 'inherit'
							}}
						>
							<FormatAlignCenterIcon />
						</IconButton>
						
						<IconButton 
							onClick={() => handleElementUpdate({
								...selectedElement,
								style: {
									...selectedElement.style,
									textAlign: 'right',
									justifyContent: 'flex-end'
								}
							})}
							sx={{ 
								bgcolor: selectedElement.style?.textAlign === 'right' ? 'rgba(238, 75, 15, 0.1)' : 'transparent',
								color: selectedElement.style?.textAlign === 'right' ? 'var(--orange)' : 'inherit'
							}}
						>
							<FormatAlignRightIcon />
						</IconButton>
					</Box>
				</Box>

				{/* Fill Type and Colors */}
				<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
					Fill Type
				</InputLabel>
				<Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
					<IconButton
						onClick={() => {
							setFillType('solid');
							handleButtonStyleChange('backgroundColor', gradientColors[0]);
						}}
						sx={{ 
							flex: 1,
							color: fillType === 'solid' ? 'var(--orange)' : 'white',
							border: `1px solid ${fillType === 'solid' ? 'var(--orange)' : 'rgba(255,255,255,0.23)'}`,
							padding: '8px'
						}}
					>
						<FormatColorFillIcon />
					</IconButton>
					<IconButton
						onClick={() => {
							setFillType('linear');
							const gradientString = `linear-gradient(${gradientAngle}deg, ${gradientColors[0]} 0%, ${gradientColors[1]} 100%)`;
							handleButtonStyleChange('backgroundColor', gradientString);
						}}
						sx={{ 
							flex: 1,
							color: fillType === 'linear' ? 'var(--orange)' : 'white',
							border: `1px solid ${fillType === 'linear' ? 'var(--orange)' : 'rgba(255,255,255,0.23)'}`,
							padding: '8px'
						}}
					>
						<GradientIcon />
					</IconButton>
					<IconButton
						onClick={() => {
							setFillType('radial');
							const gradientString = `radial-gradient(circle, ${gradientColors[0]} 0%, ${gradientColors[1]} 100%)`;
							handleButtonStyleChange('backgroundColor', gradientString);
						}}
						sx={{ 
							flex: 1,
							color: fillType === 'radial' ? 'var(--orange)' : 'white',
							border: `1px solid ${fillType === 'radial' ? 'var(--orange)' : 'rgba(255,255,255,0.23)'}`,
							padding: '8px'
						}}
					>
						<RadioButtonUncheckedIcon />
					</IconButton>
				</Box>

				{/* Color Controls */}
				{fillType === 'solid' ? (
					<Box sx={{ mb: 2 }}>
						<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
							Background Color
						</InputLabel>
						<input
							type="color"
							value={selectedElement.style?.backgroundColor || '#ffffff'}
							onChange={(e) => handleButtonStyleChange('backgroundColor', e.target.value)}
							style={{ width: '100%', height: '40px' }}
						/>
					</Box>
				) : (
					<>
						<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
							Gradient Colors
						</InputLabel>
						<Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
							<input
								type="color"
								value={gradientColors[0]}
								onChange={(e) => {
									const newColors = [e.target.value, gradientColors[1]];
									setGradientColors(newColors);
									const gradientString = `linear-gradient(${gradientAngle}deg, ${newColors[0]} 0%, ${newColors[1]} 100%)`;
									handleButtonStyleChange('backgroundColor', gradientString);
								}}
								style={{ width: '100%', height: '40px' }}
							/>
							<input
								type="color"
								value={gradientColors[1]}
								onChange={(e) => {
									const newColors = [gradientColors[0], e.target.value];
									setGradientColors(newColors);
									const gradientString = `linear-gradient(${gradientAngle}deg, ${newColors[0]} 0%, ${newColors[1]} 100%)`;
									handleButtonStyleChange('backgroundColor', gradientString);
								}}
								style={{ width: '100%', height: '40px' }}
							/>
						</Box>

						<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
							Angle
						</InputLabel>
						<Slider
							value={gradientAngle}
							onChange={(e, newValue) => {
								setGradientAngle(newValue);
								const gradientString = `linear-gradient(${newValue}deg, ${gradientColors[0]} 0%, ${gradientColors[1]} 100%)`;
								handleButtonStyleChange('backgroundColor', gradientString);
							}}
							min={0}
							max={360}
							sx={{ color: 'white', mb: 2 }}
						/>
					</>
				)}

				{/* Border Radius */}
				<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
					Border Radius
				</InputLabel>
				<Slider
					value={selectedElement.style?.borderRadius || 0}
					onChange={(e, value) => handleButtonStyleChange('borderRadius', value)}
					min={0}
					max={50}
					sx={{ color: 'white', mb: 2 }}
				/>

				{/* Rest of the button settings */}
				<Box sx={{ mb: 2 }}>
					{/* Border Color */}
					<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
						Border Color
					</InputLabel>
					<input
						type="color"
						size="small"
						value={selectedElement.style?.borderColor || '#000000'}
						onChange={(e) => handleButtonStyleChange('borderColor', e.target.value)}
						style={{ width: '100%', height: '40px', marginBottom: '16px' }}
					/>

					{/* Border Width */}
					<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
						Border Width
					</InputLabel>
					<Slider
						value={selectedElement.style?.borderWidth || 0}
						size="small"
						onChange={(e, value) => handleButtonStyleChange('borderWidth', value)}
						min={0}
						max={20}
						sx={{ color: 'white', mb: 2 }}
					/>

					{/* Border Style */}
					<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
						Border Style
					</InputLabel>
					<Select
						value={selectedElement.style?.borderStyle || 'solid'}
						onChange={(e) => handleButtonStyleChange('borderStyle', e.target.value)}
						size="small"
						fullWidth
						sx={{ bgcolor: 'white', mb: 2 }}
					>
						<MenuItem value="solid">Solid</MenuItem>
						<MenuItem value="dashed">Dashed</MenuItem>
						<MenuItem value="dotted">Dotted</MenuItem>
					</Select>
				</Box>
			</Box>
		);
	};

	// Add the delete handler function
	const handleDeleteElement = (elementId) => {
		const newBannerSizes = [...bannerSizes];
		const banner = newBannerSizes[selectedBannerIndex];
		banner.elements = banner.elements.filter(el => el.id !== elementId);
		setBannerSizes(newBannerSizes);
		if (selectedElement?.id === elementId) {
			setSelectedElement(null);
		}
	};

	// Add new state for button cell selection
	const [showButtonCellSelector, setShowButtonCellSelector] = useState(false);

	// Update handleButtonCellSelect for button elements
	const handleButtonCellSelect = (cell, value) => {
		const newBannerSizes = [...bannerSizes];
		newBannerSizes[selectedBannerIndex].elements.push({
			id: Date.now(),
			layerId: `button_${cell}`, // Add default layer ID based on cell name
			type: 'button',
			content: value,
			cell: cell,
			// Add HTML5 banner specific properties
			htmlProperties: {
				href: selectedRow.exitUrl || '#', // Store the URL from sheet data
				target: '_blank',
				// We can add more HTML5 specific properties here later
			},
			position: { x: 0, y: 0 },
			size: { width: 120, height: 40 },
			style: {
				backgroundColor: '#31cb4b',
				borderRadius: 4,
				borderWidth: 0,
				borderStyle: 'solid',
				borderColor: '#2cb543',
				color: '#ffffff',
				fontSize: 16,
				fontFamily: 'Roboto',
				padding: { top: 4, right: 4, bottom: 4, left: 4 },
				cursor: 'pointer',
				textDecoration: 'none',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			}
		});
		setBannerSizes(newBannerSizes);
		setShowButtonCellSelector(false);
	};

	// Add ButtonCellSelector component
	const renderButtonCellSelector = () => {
		if (!selectedRow || selectedBannerIndex === null || !bannerSizes[selectedBannerIndex]) return null;

		// Get all used cells in the current banner
		const usedCells = bannerSizes[selectedBannerIndex].elements
			.map(element => element.cell);

		// Filter cells that could be button text (e.g., CTA text)
		const buttonCells = Object.entries(selectedRow.cells).filter(([cell, value]) => {
			return !cell.includes('Color') && 
						 !usedCells.includes(cell) &&
						 (cell.toLowerCase().includes('button') || 
							cell.toLowerCase().includes('cta') ||
							cell.toLowerCase().includes('link'));
		});

		return (
			<Dialog 
				open={showButtonCellSelector} 
				onClose={() => setShowButtonCellSelector(false)}
				maxWidth="sm"
				fullWidth
				className="text-selector-dialog"
			>
				<DialogTitle>Select Button Text from Sheet</DialogTitle>
				<DialogContent>
					<List>
						{buttonCells.length > 0 ? (
							buttonCells.map(([cell, value]) => (
								<MuiMenuItem 
									key={cell}
									onClick={() => handleButtonCellSelect(cell, value)}
								>
									<ListItemText 
										primary={cell}
										secondary={value}
										primaryTypographyProps={{
											style: {
												fontWeight: 500,
												fontSize: '16px'
											}
										}}
										secondaryTypographyProps={{
											style: {
												fontSize: '13px',
												color: 'rgba(0, 0, 0, 0.6)'
											}
										}}
									/>
								</MuiMenuItem>
							))
						) : (
							<Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
								No available button text cells found
							</Typography>
						)}
					</List>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setShowButtonCellSelector(false)}>Cancel</Button>
				</DialogActions>
			</Dialog>
		);
	};

	// Add new state for layer management
	const [showLayersPanel, setShowLayersPanel] = useState(false);
	const [selectedBannerForLayers, setSelectedBannerForLayers] = useState(null);

	// Add new state for temporary layer ordering
	const [tempElements, setTempElements] = useState(null);

	// Update the layer management functions
	const handleLayerClick = (bannerIndex) => {
		setSelectedBannerForLayers(bannerIndex);
		// Reverse the elements when showing in the layer panel
		setTempElements([...bannerSizes[bannerIndex].elements].reverse());
		setShowLayersPanel(true);
	};

	const moveLayer = (elementIndex, direction) => {
		const elements = [...tempElements];
		
		if (direction === 'up' && elementIndex > 0) {
			// Moving up means moving towards index 0 (higher z-index)
			[elements[elementIndex], elements[elementIndex - 1]] = 
			[elements[elementIndex - 1], elements[elementIndex]];
		} else if (direction === 'down' && elementIndex < elements.length - 1) {
			// Moving down means moving away from index 0 (lower z-index)
			[elements[elementIndex], elements[elementIndex + 1]] = 
			[elements[elementIndex + 1], elements[elementIndex]];
		}
		
		setTempElements(elements);
	};

	// Add function to apply layer changes
	const handleApplyLayers = () => {
		if (selectedBannerForLayers === null || !tempElements) return;

		const updatedBannerSizes = [...bannerSizes];
		updatedBannerSizes[selectedBannerForLayers].elements = tempElements.map(element => ({
			...element,
			style: {
				...element.style,
				visibility: element.style?.visibility || 'visible'
			}
		}));

		setBannerSizes(updatedBannerSizes);
		setShowLayersPanel(false);
		setTempElements(null);
		setSelectedBannerForLayers(null);
	};

	// Add function to cancel layer changes
	const handleCancelLayers = () => {
		setShowLayersPanel(false);
		setTempElements(null);
	};

	// Add new state for editing layer names
	const [editingLayerId, setEditingLayerId] = useState(null);

	// Update handleLayerNameChange to store the new name as layerId
	const handleLayerNameChange = (elementId, newName) => {
		const elements = [...tempElements];
		const elementIndex = elements.findIndex(el => el.id === elementId);
		
		if (elementIndex !== -1) {
			elements[elementIndex] = {
				...elements[elementIndex],
				layerId: newName // Store the new name as layerId
			};
			setTempElements(elements);
		}
		setEditingLayerId(null);
	};

	// Update the renderLayersPanel component to include editable names
	const renderLayersPanel = () => {
		if (!showLayersPanel || selectedBannerForLayers === null || !tempElements) return null;

		const getLayerName = (element) => {
			// If element has a layerId, use it, otherwise generate a default name
			if (element.layerId) return element.layerId;

			// Generate default names based on element type
			switch (element.type) {
				case 'text':
					return `Text: ${element.content.substring(0, 20)}${element.content.length > 20 ? '...' : ''}`;
				case 'button':
					return `Button: ${element.content.substring(0, 20)}${element.content.length > 20 ? '...' : ''}`;
				case 'shape':
					return `Shape: ${element.shapeType}`;
				case 'image':
					return `Image ${element.id}`;
				default:
					return `Layer ${element.id}`;
			}
		};

		return (
			<Dialog
				open={showLayersPanel}
				onClose={handleCancelLayers}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>
					Layer Management
					<IconButton
						onClick={handleCancelLayers}
						sx={{ position: 'absolute', right: 8, top: 8 }}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					<List>
						{tempElements.map((element, index) => (
							<ListItem
								key={element.id}
								sx={{
									border: '1px solid rgba(0,0,0,0.12)',
									borderRadius: 1,
									mb: 1,
									bgcolor: selectedElement?.id === element.id ? 'rgba(238, 75, 15, 0.08)' : 'transparent',
									opacity: element.style?.visibility === 'hidden' ? 0.5 : 1 // Visual feedback
								}}
								secondaryAction={
									<Box sx={{ 
										display: 'flex', 
										flexDirection: 'column', 
										gap: 0.5 
									}}>
										{/* First row: arrows and visibility */}
										<Box sx={{ display: 'flex', gap: 1 }}>
											<IconButton
												edge="end"
												size="small"
												disabled={index === 0}
												onClick={() => moveLayer(index, 'up')}
											>
												<ArrowUpwardIcon />
											</IconButton>
											<IconButton
												edge="end"
												size="small"
												disabled={index === tempElements.length - 1}
												onClick={() => moveLayer(index, 'down')}
											>
												<ArrowDownwardIcon />
											</IconButton>
											<IconButton
												edge="end"
												size="small"
												onClick={() => {
													handleLayerAction('toggleVisibility', element.id);
												}}
												sx={{ 
													color: element.style?.visibility === 'hidden' ? 'text.disabled' : 'text.primary'
												}}
											>
												{element.style?.visibility === 'hidden' ? <VisibilityOffIcon /> : <VisibilityIcon />}
											</IconButton>
										</Box>

										{/* Second row: duplicate and delete */}
										<Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
											<IconButton
												edge="end"
												size="small"
												onClick={() => handleLayerAction('duplicate', element.id)}
											>
												<ContentCopyIcon />
											</IconButton>
											<IconButton
												edge="end"
												size="small"
												onClick={() => handleDeleteElement(element.id)}
												sx={{ color: 'error.main' }}
											>
												<DeleteIcon />
											</IconButton>
										</Box>
									</Box>
								}
							>
								{editingLayerId === element.id ? (
									<TextField
										autoFocus
										defaultValue={element.layerId || getLayerName(element)}
										onBlur={(e) => handleLayerNameChange(element.id, e.target.value)}
										onKeyPress={(e) => {
											if (e.key === 'Enter') {
												handleLayerNameChange(element.id, e.target.value);
											}
										}}
										size="small"
										fullWidth
										sx={{ mr: 2 }}
									/>
								) : (
									<ListItemText
										primary={element.layerId || getLayerName(element)}
										onClick={() => setEditingLayerId(element.id)}
										sx={{ 
											cursor: 'pointer',
											pr: 2,
											'&:hover': {
												'& .edit-icon': {
													opacity: 1
												}
											}
										}}
										secondary={
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
												<EditIcon 
													className="edit-icon"
													sx={{ 
														fontSize: 14,
														opacity: 0,
														transition: 'opacity 0.2s',
														color: 'text.secondary'
													}}
												/>
												<Typography variant="caption" color="text.secondary">
													Click to rename
												</Typography>
											</Box>
										}
									/>
								)}
							</ListItem>
						))}
					</List>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelLayers}>Cancel</Button>
					<Button 
						onClick={handleApplyLayers}
						variant="contained"
						sx={{ 
							bgcolor: 'var(--orange)',
							'&:hover': {
								bgcolor: 'var(--orange)',
								opacity: 0.9
							}
						}}
					>
						Apply Changes
					</Button>
				</DialogActions>
			</Dialog>
		);
	};

	// Update the handleOutsideClick function
	const handleOutsideClick = (e) => {
		// Check if click is outside banner-preview and not in any of these areas:
		// - Drawer (left panel) and its content
		// - Alignment tools
		// - Any dialog
		// - Any popover/menu
		if (!e.target.closest('.banner-preview') && 
				!e.target.closest('.MuiDrawer-root') &&	// This excludes the Drawer
				!e.target.closest('.MuiDrawer-paper') &&	// This excludes the Drawer content
				!e.target.closest('.alignment-tools') &&
				!e.target.closest('.MuiDialog-root') &&
				!e.target.closest('.MuiPopover-root')) {
			setSelectedElement(null);
			setSelectedElements([]);
		}
	};

	// Update the handleBannerClick function to be more specific
	const handleBannerClick = (e) => {
		// Only deselect if clicking directly on the banner background
		// and not on any element or control
		if (e.target.className === 'banner-preview' && 
				!e.target.closest('.txt_constraint') && 
				!e.target.closest('.svg-element') && 
				!e.target.closest('.button-element')) {
			setSelectedElement(null);
			setSelectedElements([]);
		}
	};

	// Add useEffect to handle outside clicks
	useEffect(() => {
		document.addEventListener('mousedown', handleOutsideClick);
		return () => {
			document.removeEventListener('mousedown', handleOutsideClick);
		};
	}, []);

	// Add new state for shape selection
	const [showShapeSelector, setShowShapeSelector] = useState(false);

	// Update the drawing state
	const [isDrawingMode, setIsDrawingMode] = useState(false);
	const [drawingPoints, setDrawingPoints] = useState([]);
	const [currentBannerDrawing, setCurrentBannerDrawing] = useState(null);
	const [isDraggingHandle, setIsDraggingHandle] = useState(false);
	const [lastMousePosition, setLastMousePosition] = useState(null);

	// Update handleShapeSelect to include a meaningful default layerId
	const handleShapeSelect = (shapeType) => {
		if (shapeType === 'path') {
			setIsDrawingMode(true);
			setShowShapeSelector(false);
		} else {
			const newBannerSizes = [...bannerSizes];
			const timestamp = Date.now();
			newBannerSizes[selectedBannerIndex].elements.push({
				id: timestamp,
				layerId: `${shapeType}_${timestamp}`, // Add a unique layerId
				type: 'shape',
				shapeType: shapeType,
				position: { x: 0, y: 0 },
				size: { width: 100, height: 100 },
				style: {
					fill: '#ffffff',
					stroke: '#000000',
					strokeWidth: 2,
					opacity: 1,
					visibility: 'visible' // Add initial visibility
				}
			});
			setBannerSizes(newBannerSizes);
		}
	};

	// New SVG-specific handlers
	const handleSVGDrag = (bannerId, elementId, e, position) => {
		const newBannerSizes = [...bannerSizes];
		const banner = newBannerSizes[bannerId];
		const elementIndex = banner.elements.findIndex(el => el.id === elementId);
		
		if (elementIndex !== -1) {
			banner.elements[elementIndex].position = position;
			setBannerSizes(newBannerSizes);
		}
	};

	const handleSVGResize = (bannerId, elementId, e, updates) => {
		const newBannerSizes = [...bannerSizes];
		const banner = newBannerSizes[bannerId];
		const elementIndex = banner.elements.findIndex(el => el.id === elementId);
		
		if (elementIndex !== -1) {
			if (updates.size) {
				banner.elements[elementIndex].size = updates.size;
			}
			if (updates.position) {
				banner.elements[elementIndex].position = updates.position;
			}
			setBannerSizes(newBannerSizes);
		}
	};

	// Update the drawing handlers
	const handleBannerMouseDown = (e, bannerId) => {
		if (!isDrawingMode) {
			handleBannerClick(e);
			return;
		}

		const rect = e.currentTarget.getBoundingClientRect();
		const x = e.clientX - rect.left;
		const y = e.clientY - rect.top;

		setLastMousePosition({ x, y });
		setIsDraggingHandle(true);

		if (drawingPoints.length === 0) {
			// First point
			setDrawingPoints([{ x, y, type: 'point' }]);
			setCurrentBannerDrawing(bannerId);
		} else if (currentBannerDrawing === bannerId) {
			// For subsequent points, we'll swap the vector directions
			const lastPoint = drawingPoints[drawingPoints.length - 1];
			
			// Calculate vector from last point to current point
			const vectorX = x - lastPoint.x;
			const vectorY = y - lastPoint.y;
			
			// For subsequent points, swap the handles direction
			// handle1 should point back to the previous point's direction
			const handle1X = x - vectorX;	// Changed from + to -
			const handle1Y = y - vectorY;	// Changed from + to -
			
			// handle2 should point in the direction we're moving
			const handle2X = x + vectorX;	// Changed from - to +
			const handle2Y = y + vectorY;	// Changed from - to +

			setDrawingPoints(prev => [
				...prev,
				{
					x,
					y,
					type: 'curve',
					handle1: { x: handle1X, y: handle1Y },	// Points back to previous point
					handle2: { x: handle2X, y: handle2Y }	 // Points in movement direction
				}
			]);
		}
	};

	const handleBannerMouseMove = (e, bannerId) => {
		if (!isDrawingMode || !isDraggingHandle || currentBannerDrawing !== bannerId) return;

		const rect = e.currentTarget.getBoundingClientRect();
		const x = e.clientX - rect.left;
		const y = e.clientY - rect.top;

		if (lastMousePosition && drawingPoints.length > 0) {
			const dx = x - lastMousePosition.x;
			const dy = y - lastMousePosition.y;
			
			// If mouse has moved more than 5 pixels, convert to curved point
			if (Math.sqrt(dx * dx + dy * dy) > 5) {
				const lastIndex = drawingPoints.length - 1;
				const lastPoint = drawingPoints[lastIndex];
				
				// Calculate vector from anchor point to mouse position
				const vectorX = x - lastPoint.x;
				const vectorY = y - lastPoint.y;
				
				// Keep handle positions the same but swap their roles in the curve
				const handle1X = x;	// Mouse position
				const handle1Y = y;
				const handle2X = lastPoint.x - vectorX;	// Opposite direction
				const handle2Y = lastPoint.y - vectorY;

				const newPoints = [...drawingPoints];
				newPoints[lastIndex] = {
					...lastPoint,
					type: 'curve',
					// Swap handle1 and handle2 in the curve data to reverse the curve direction
					handle1: { x: handle2X, y: handle2Y },	// Use opposite point for handle1
					handle2: { x: handle1X, y: handle1Y }	 // Use mouse position for handle2
				};
				
				setDrawingPoints(newPoints);
			}
		}
	};

	const handleBannerMouseUp = () => {
		setIsDraggingHandle(false);
		setLastMousePosition(null);
	};

	// Also update the path creation in handleBannerDoubleClick
	const handleBannerDoubleClick = (bannerId) => {
		if (!isDrawingMode || currentBannerDrawing !== bannerId) return;

		if (drawingPoints.length >= 2) {
			const timestamp = Date.now();
			const newBannerSizes = [...bannerSizes];
			newBannerSizes[bannerId].elements.push({
				id: timestamp,
				layerId: `path_${timestamp}`, // Add a unique layerId for paths
				type: 'shape',
				shapeType: 'path',
				// ... rest of the element properties ...
			});
			setBannerSizes(newBannerSizes);
		}
		// ... rest of the function ...
	};

	// Add new state for shape styling
	const [shapeFillType, setShapeFillType] = useState('solid');
	const [shapeGradientStops, setShapeGradientStops] = useState([
		{ position: 0, color: '#ffffff' },
		{ position: 100, color: '#000000' }
	]);
	const [shapeGradientAngle, setShapeGradientAngle] = useState(90);
	const [shapeStrokeWidth, setShapeStrokeWidth] = useState(2);
	const [shapeStrokeColor, setShapeStrokeColor] = useState('#000000');
	const [shapeStrokeStyle, setShapeStrokeStyle] = useState('none'); // Add this
	const [shapeCornerRadius, setShapeCornerRadius] = useState(0);
	const [shapeFillColor, setShapeFillColor] = useState('#ffffff');
	const [shapeOpacity, setShapeOpacity] = useState(100);
	const [shapeMixBlendMode, setShapeMixBlendMode] = useState('normal'); // Add this

	// Add Shape Settings component
	const ShapeSettings = () => {
		if (!selectedElement?.type === 'shape') return null;

		// Update the handleShapeStyleChange function to handle gradient properties correctly
		const handleShapeStyleChange = (property, value) => {
			if (!selectedElement) return;

			// Create a deep copy of the selected element
			const updatedElement = {
				...selectedElement,
				style: {
					...selectedElement.style,
					[property]: value
				}
			};

			// Update the element in bannerSizes
			const updatedBannerSizes = bannerSizes.map((banner, index) => {
				if (index === selectedBannerIndex) {
					return {
						...banner,
						elements: banner.elements.map(element => 
							element.id === selectedElement.id ? updatedElement : element
						)
					};
				}
				return banner;
			});

			// Update both states to trigger re-render
			setBannerSizes(updatedBannerSizes);
			setSelectedElement(updatedElement);
		};

		const getShapeGradientString = () => {
			if (shapeFillType === 'linear') {
				return `linear-gradient(${shapeGradientAngle}deg, ${shapeGradientStops.map(stop => 
					`${stop.color} ${stop.position}%`).join(', ')})`;
			}
			if (shapeFillType === 'radial') {
				return `radial-gradient(circle, ${shapeGradientStops.map(stop => 
					`${stop.color} ${stop.position}%`).join(', ')})`;
			}
			return shapeFillColor;
		};

		return (
			<Box className="database-section">
				{/* ... other settings ... */}

				{/* Mix Blend Mode Dropdown */}
				<Box sx={{ mb: 2 }}>
					<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
						Mix Blend Mode
					</InputLabel>
					<Select
						value={selectedElement.style?.mixBlendMode || 'normal'}
						onChange={(e) => handleShapeStyleChange('mixBlendMode', e.target.value)}
						size="small"
						fullWidth
						sx={{ bgcolor: 'white' }}
					>
						<MenuItem value="normal">Normal</MenuItem>
						<MenuItem value="multiply">Multiply</MenuItem>
						<MenuItem value="screen">Screen</MenuItem>
						<MenuItem value="overlay">Overlay</MenuItem>
						<MenuItem value="darken">Darken</MenuItem>
						<MenuItem value="lighten">Lighten</MenuItem>
						<MenuItem value="color-dodge">Color Dodge</MenuItem>
						<MenuItem value="color-burn">Color Burn</MenuItem>
						<MenuItem value="hard-light">Hard Light</MenuItem>
						<MenuItem value="soft-light">Soft Light</MenuItem>
						<MenuItem value="difference">Difference</MenuItem>
						<MenuItem value="exclusion">Exclusion</MenuItem>
					</Select>
				</Box>

				{/* Add Opacity Slider */}
				<Box sx={{ mb: 2 }}>
					<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
						Opacity
					</InputLabel>
					<Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
						<Slider
							value={(selectedElement.style?.opacity || 1) * 100}
							onChange={(e, value) => handleShapeStyleChange('opacity', value / 100)}
							min={0}
							max={100}
							sx={{ 
								color: 'white',
								width: '140px',
								'& .MuiSlider-thumb': {
									backgroundColor: 'white',
								},
								'& .MuiSlider-track': {
									backgroundColor: 'white',
								},
								'& .MuiSlider-rail': {
									backgroundColor: 'rgba(255,255,255,0.3)',
								}
							}}
						/>
						<TextField
							size="small"
							value={Math.round((selectedElement.style?.opacity || 1) * 100)}
							onChange={(e) => {
								const value = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
								handleShapeStyleChange('opacity', value / 100);
							}}
							InputProps={{
								endAdornment: <InputAdornment position="end">%</InputAdornment>,
								sx: { bgcolor: 'white' }
							}}
							sx={{ width: '80px' }}
						/>
					</Box>
				</Box>

				{/* ... other settings ... */}
			</Box>
		);
	};

	// Update the handleMaskChange function with simpler debug
	const handleMaskChange = (maskId) => {
		// Find the shape being used as mask
		const maskShape = maskId ? 
			bannerSizes[selectedBannerIndex].elements.find(el => 
				el.id === maskId
			) : null;
		
		const maskShapeLayerId = maskShape?.layerId || null;
		
		// Store the layerId in state
		setCurrentMaskLayerId(maskShapeLayerId);

		if (!selectedElement) return;

		const newBannerSizes = [...bannerSizes];
		const banner = newBannerSizes[selectedBannerIndex];
		const elementIndex = banner.elements.findIndex(el => el.id === selectedElement.id);

		if (elementIndex !== -1) {
			banner.elements[elementIndex] = {
				...banner.elements[elementIndex],
				style: {
					...banner.elements[elementIndex].style,
					maskId: maskId || null,
					maskLayerId: maskShapeLayerId	// Store it in the element's style too
				}
			};
		}

		setBannerSizes(newBannerSizes);
	};

	// Add this function to handle the HTML export
	const exportBannerHTML = (banner) => {
		const bannerName = bannerNames[`${banner.width}x${banner.height}`] || 'Banner';

		// First collect all used fonts (keep existing code)
		const usedFonts = new Set();
		banner.elements.forEach(element => {
			if (element.style?.fontFamily) {
					usedFonts.add(element.style.fontFamily);
			}
		});

		// Generate font links (keep existing code)
		const fontLinks = Array.from(usedFonts)
			.map(font => `<link href="https://fonts.googleapis.com/css2?family=${font.replace(' ', '+')}&display=swap" rel="stylesheet">`)
			.join('\n');

		// Generate HTML for each element, including hidden ones
		const elementsHTML = banner.elements.map(element => {
			// Get the visibility style
			const visibility = element.style?.visibility || 'visible';
			
			switch (element.type) {
				case 'text':
					const txtElement = document.querySelector(`#${element.layerId} .txt`);
					const txtComputedStyle = txtElement ? window.getComputedStyle(txtElement) : null;
					const txtActualFontSize = txtComputedStyle?.fontSize;

					return `<div id="${element.layerId}" class="abszolut" style="left:${element.position.x}px;top:${element.position.y}px;width:${element.size.width}px;height:${element.size.height}px;visibility:${visibility};">
		<div class="txt_container" style="visibility:${visibility};">
			<div class="txt" style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-family:${element.style?.fontFamily || 'Roboto'};font-size:${txtActualFontSize};text-align:${element.style?.textAlign || 'center'};color:${element.style?.color || '#000000'};text-transform:${element.style?.textTransform || 'none'};font-weight:${element.style?.fontWeight || 'normal'};font-style:${element.style?.fontStyle || 'normal'};line-height:${element.style?.lineHeight || '1'};transform:${element.style?.transform || 'none'};transform-origin:center center;white-space:pre-wrap;word-break:break-word;visibility:${visibility};">${element.content}</div>
		</div>
	</div>`;

				case 'button':
					const btnElement = document.querySelector(`#${element.layerId} .txt`);
					const btnComputedStyle = btnElement ? window.getComputedStyle(btnElement) : null;
					const btnActualFontSize = btnComputedStyle?.fontSize;
					const btnTransform = btnComputedStyle?.transform || 'none';

					return `<div id="${element.layerId}" class="abszolut" style="left:${element.position.x}px;top:${element.position.y}px;width:${element.size.width}px;height:${element.size.height}px;visibility:${visibility};">
		<div class="txt_container" style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:${element.style?.backgroundColor || '#ffffff'};border-radius:${element.style?.borderRadius || 0}px;border:${element.style?.borderWidth || 0}px ${element.style?.borderStyle || 'solid'} ${element.style?.borderColor || '#000000'};transform-origin:center center;visibility:${visibility};">
			<div class="txt" style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:${element.style?.color || '#000000'};font-size:${btnActualFontSize};font-family:${element.style?.fontFamily || 'Roboto'};font-weight:${element.style?.fontWeight || 'normal'};font-style:${element.style?.fontStyle || 'normal'};padding:${element.style?.padding?.top || 0}px ${element.style?.padding?.right || 0}px ${element.style?.padding?.bottom || 0}px ${element.style?.padding?.left || 0}px;text-align:center;line-height:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;box-sizing:border-box;transform:${btnTransform};transform-origin:center center;visibility:${visibility};">${element.content}</div>
		</div>
	</div>`;

				case 'image':
					return `<div id="${element.layerId}" class="abszolut" style="left:${element.position.x}px;top:${element.position.y}px;width:${element.size.width}px;height:${element.size.height}px;visibility:${visibility};">
		<img src="${element.content}" alt="${element.layerId}" style="width:100%;height:100%;object-fit:${element.style?.objectFit || 'contain'};opacity:${element.style?.opacity || 1};mix-blend-mode:${element.style?.mixBlendMode || 'normal'};mask:${element.style?.maskLayerId ? `url(#${element.style.maskLayerId}Mask)` : 'none'};-webkit-mask:${element.style?.maskLayerId ? `url(#${element.style.maskLayerId}Mask)` : 'none'};visibility:${visibility};">
	</div>`;

				case 'shape':
					const isMask = banner.elements.some(el => 
						el.type === 'image' && 
						el.style?.maskId === element.id &&
						el.style?.visibility !== 'hidden'	// Only consider visible images
					);

					// Calculate stroke width
					const strokeWidth = element.style?.strokeWidth || 0;

					// Get shape element
					const getShapeElement = () => {
						const shapeAttrs = `fill="${element.style?.fillType === 'solid' ? (element.style?.fill || '#000000') : `url(#${element.layerId}Gradient)`}" stroke="${element.style?.stroke || 'none'}" stroke-width="${strokeWidth}" stroke-dasharray="${element.style?.strokeStyle === 'dashed' ? `${strokeWidth * 2},${strokeWidth}` : 
																element.style?.strokeStyle === 'dotted' ? `${strokeWidth},${strokeWidth}` : 'none'}" opacity="${element.style?.opacity || 1}" visibility="${element.style?.visibility || 'visible'}"`;

						switch (element.shapeType) {
							case 'rectangle':
								return `<rect x="${strokeWidth / 2}" y="${strokeWidth / 2}" width="${element.size.width - strokeWidth}" height="${element.size.height - strokeWidth}" rx="${element.style?.cornerRadius || 0}" ry="${element.style?.cornerRadius || 0}" ${shapeAttrs}/>`;
							case 'circle':
								const diameter = Math.min(element.size.width, element.size.height) - strokeWidth;
								return `<circle cx="${element.size.width / 2}" cy="${element.size.height / 2}" r="${diameter / 2}" ${shapeAttrs}/>`;
							case 'triangle':
								const points = `${strokeWidth/2},${element.size.height - strokeWidth/2} ${element.size.width/2},${strokeWidth/2} ${element.size.width - strokeWidth/2},${element.size.height - strokeWidth/2}`;
								return `<polygon points="${points}" ${shapeAttrs}/>`;
							default:
								return '';
						}
					};

					return `<div id="${element.layerId}" class="abszolut" style="left:${element.position.x}px;top:${element.position.y}px;width:${element.size.width}px;height:${element.size.height}px;mix-blend-mode:${element.style?.mixBlendMode || 'normal'};visibility:${visibility};">
		<svg width="100%" height="100%" viewBox="0 0 ${element.size.width} ${element.size.height}" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" style="overflow:visible;visibility:${visibility};"><defs>${element.style?.fillType !== 'solid' ? generateGradientDefs(element) : ''}</defs>${isMask ? `<mask id="${element.layerId}Mask">${getShapeElement()}</mask>` : getShapeElement()}</svg>
	</div>`;

				default:
					return '';
			}
		}).join('\n');

		const scripts = getSelectedLibraryScripts();

		// Process each selected clickTag
		selectedClickTags.forEach(clickTag => {
			if (clickTag === 'no CT') return;

			let clickTagDefinition = '';
			let clickTagButton = '';
			let additionalScript = '';
			let endScript = '';

			switch(clickTag) {
				case 'Google Ads':
					clickTagDefinition = `
		<!--   Google ADWORDS definition   -->
		<meta name="ad.size" content="width=${banner.width},height=${banner.height}">
		<meta name="ad.format" content="adwords">`;
					clickTagButton = `<a id="creativelink" onclick="" style="width:100%; height:100%; display: block; cursor: pointer;"><!--   ADWORDS	 -->`;
					break;
				
				case 'Google DC360':
					clickTagDefinition = `
		<!--   Google DOUBLECLICK definition   -->
		<meta name="ad.size" content="width=${banner.width},height=${banner.height}">
		<script type="text/javascript">
			var clickTag = "https://google.com";
		</script>`;
					clickTagButton = `<a id="creativelink" href="javascript:window.open(window.clickTag)" style="width:100%; height:100%; display: block; cursor: pointer;"><!-- DOUBLECLICK -->`;
					break;
				
				case 'Gemius':
					clickTagDefinition = `
		<!--   GEMIUS definition   -->
		<meta name="ad.size" content="width=${banner.width},height=${banner.height}">`;
					clickTagButton = `<a id="creativelink" target="_blank" style="width:100%; height:100%; display: block; cursor: pointer;"> <!--   GEMIUS	 -->`;
					endScript = `
    <script type="text/javascript">
        /*** Gemius ***/
        var parsed = (document.location.href.split('#')[1]||'').split('&');
        var params = parsed.reduce(function (params, param) {
            var param = param.split('=');
            params[param[0]] = decodeURIComponent(param.slice(1).join('='));
            return params;
        }, {});
        
        // change link href
        document.getElementById('creativelink').href = params.clickTag;
    </script>`;
					break;

				case 'Adverticum':
					clickTagDefinition = `
		<!--   ADVERTICUM definition   -->
		<meta name="ad.size" content="width=${banner.width},height=${banner.height}">`;
					clickTagButton = `<a id="clickTAG" href="" target="" style="width:100%; height:100%; display: block; cursor: pointer;"><!--   ADVERTICUM   -->`;
					additionalScript = `<script src="https://ad.adverticum.net/scripts/goa-helper.js"></script>		 				<!-- ADVERTICUM	 -->`;
					endScript = `
    <script type="text/javascript">
        /*** Adverticum ***/
        document.getElementById('clickTAG').onclick = function () {
            window.open(goa.clickTAG, goa.clickTARGET);
        }
    </script>`;
					break;

				case 'AdForm':
					clickTagDefinition = `
		<!--   ADFORM definition   -->
		<meta http-equiv='X-UA-Compatible' content='IE=edge'>
		<meta name="ad.size" content="width=${banner.width},height=${banner.height}">`;
					clickTagButton = `<a id="creativelink" target="_blank" style="width:100%; height:100%; display: block; cursor: pointer;"> <!--   ADFORM	 -->`;
					additionalScript = `<script>document.write('<script src=\"' + (window.API_URL || 'https://s1.adform.net/banners/scripts/rmb/Adform.DHTML.js?bv=' + Math.random()) + '\"\>\<\\\/script\>\');</script> <!-- ADFORM -->`;
					endScript = `
    <script type="text/javascript">
        /*** AdForm ***/
        var banner = document.getElementById('mainContainer');
        clickTAGvalue = dhtml.getVar('clickTAG', "https://google.com");
        landingpagetarget = dhtml.getVar('landingPageTarget', '_blank');
        
        banner.onclick = function() {
            window.open(clickTAGvalue, landingpagetarget);
        }
    </script>`;
					break;
			}

			// Generate HTML with clickTag button, additional scripts, and end scripts
			const html = `<!doctype html>
<html>
<head>
	<meta name="generator" content="High Voltage adShaper v0.99 @ ${formatDate()}">
	${clickTagDefinition}
	<title>Banner ${banner.width}x${banner.height}</title>
	${fontLinks}
	${scripts}
	${additionalScript}
</head>
<body>
	<style>
		/* CSS RESET */
		html,body,div,span,applet,object,iframe,h1,h2,h3,h4,h5,h6,p,blockquote,pre,a,abbr,acronym,address,big,cite,code,del,dfn,em,img,ins,kbd,q,s,samp,small,strike,strong,sub,sup,tt,var,b,u,i,center,dl,dt,dd,ol,ul,li,fieldset,form,label,legend,table,caption,tbody,tfoot,thead,tr,th,td,article,aside,canvas,details,embed,figure,figcaption,footer,header,hgroup,menu,nav,output,ruby,section,summary,time,mark,audio,video{margin:0;padding:0;border:0;font-size:100%;font:inherit;vertical-align:baseline;}article,aside,details,figcaption,figure,footer,header,hgroup,menu,nav,section{display:block;}body{line-height:1;}ol,ul{list-style:none;}blockquote,q{quotes:none;}blockquote:before,blockquote:after,q:before,q:after{content:'';content:none;}table{border-collapse:collapse;border-spacing:0;}

		/* BASIC DEFINITIONS */
		.abszolut{pointer-events:none;position:absolute;left:0;top:0;mix-blend-mode:normal;will-change:transform,opacity;overflow:hidden;}
		.normal{pointer-events:none;position:absolute;left:0;top:0;mix-blend-mode:normal;will-change:transform,opacity;}
		.txt_container{width:100%;height:100%;display:flex;align-items:center;justify-content:center;padding:0;box-sizing:border-box;overflow:hidden;background:transparent;}
		.txt{width:100%;display:block;text-align:center;margin:0;padding:0;transform-origin:center center;white-space:pre-wrap;word-break:break-word;}
		#mainContainer{position:relative;overflow:hidden;width:${banner.width}px;height:${banner.height}px;background-color:${banner.backgroundColor || '#f8f8f8'};}
		#STAGE{position:absolute;width:${banner.width}px;height:${banner.height}px;top:0;left:0;}
		${generateAnimationFaderCss(banner)}
		#STROKE{position:absolute;width:${banner.width}px;height:${banner.height}px;top:0;left:0;box-shadow:inset 0 0 0 1px rgba(0, 0, 0, 0.12);}
	</style>

	<div id="mainContainer">
		${clickTagButton}
		<div id="STAGE">
			${elementsHTML}
		</div>
		${selectedAnimationLibrary === 'GSAP' ? '<div id="FADER" class="abszolut"></div>' : 'aaa'}
		<div id="STROKE" class="abszolut"></div>
		</a>
	</div>
	${selectedAnimationLibrary === 'GSAP' ? generateAnimationScript(banner, {
		enabled: true,
		speed: parseFloat(animationSpeed),
		interval: parseFloat(animationInterval),
		delay: parseFloat(animationDelay),
		pause: parseFloat(animationPause),
		back: parseFloat(animationBack),
		backInout: parseFloat(animationBackInout),
		timeScale:parseFloat(animationTimeScale),
	}) : ''}
	${endScript}
</body>
</html>`;

			// Create filename with clickTag type
			const cleanClickTagName = clickTag.replace(/\s+/g, '');
			const fileName = `${banner.width}x${banner.height}_${bannerName}_${cleanClickTagName}.html`;
			
			// Download the file
			downloadFile(html, fileName);
		});

		console.groupEnd();
	};

	// Update the download handler to use the new export function
	const handleDownloadBanner = (banner, index) => {
		if (!banner) {
			console.error('No banner selected');
			return;
		}

		const html = exportBannerHTML(banner);
		const blob = new Blob([html], { type: 'text/html' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		
		// Get the dimensions key
		const dimensionKey = `${banner.width}x${banner.height}`;
		
		// Get the banner name or use default
		const bannerName = bannerNames[dimensionKey]?.replace(/[^a-z0-9]/gi, '_') || 'banner';
		
		// Create filename
		const fileName = `${dimensionKey}_${bannerName}.html`;
		
		a.download = fileName;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	};

	// Add helper function for gradient definitions
	const generateGradientDefs = (element) => {
		const style = element.style || {};
		if (!style.fillType || style.fillType === 'solid') return '';

		if (style.fillType === 'linear') {
			return `<linearGradient id="${element.layerId}Gradient" x1="0%" y1="0%" x2="0%" y2="100%" gradientTransform="rotate(${style.gradientAngle || 0}, 0.5, 0.5)">${style.gradientStops.map(stop => `<stop offset="${stop.offset}%" stop-color="${stop.color}" stop-opacity="${stop.opacity}"/>`).join('')}</linearGradient>`;
		}

		return `<radialGradient id="${element.layerId}Gradient" cx="50%" cy="50%" r="50%" gradientUnits="userSpaceOnUse">${style.gradientStops.map(stop => `<stop offset="${stop.offset}%" stop-color="${stop.color}" stop-opacity="${stop.opacity}"/>`).join('')}</radialGradient>`;
	};

	// Update the generateSVGShape function to include all style properties
	const generateSVGShape = (element, fillValue) => {
		const style = element.style || {};
		const commonProps = `
			fill="${fillValue}"
			stroke="${style.stroke || '#000000'}"
			stroke-width="${style.strokeWidth || 2}"
			opacity="${style.opacity || 1}"
			style="mix-blend-mode:${style.mixBlendMode || 'normal'}"
			${style.strokeDasharray ? `stroke-dasharray="${style.strokeDasharray}"` : ''}
		`.trim();

		switch (element.shapeType) {
			case 'rectangle':
				return `<rect ${commonProps} width="${element.size.width}" height="${element.size.height}" rx="${style.cornerRadius || 0}" ry="${style.cornerRadius || 0}"/>`;
			case 'circle':
				const radius = Math.min(element.size.width, element.size.height) / 2;
				return `<circle ${commonProps} cx="${element.size.width/2}" cy="${element.size.height/2}" r="${radius}"/>`;
			case 'path':
				return `<path ${commonProps} d="${element.pathData}"/>`;
			// Add other shape types as needed
			default:
				return '';
		}
	};

	// Add new state for default settings
	const [defaultBackgroundColor, setDefaultBackgroundColor] = useState('#f8f8f8');
	const [defaultBorderColor, setDefaultBorderColor] = useState('rgba(0, 0, 0, 0.12)');

	// Add the handler functions
	const handleBackgroundColorChange = (color) => {
		setDefaultBackgroundColor(color);
		// Update all banners with new background color
		const newBannerSizes = bannerSizes.map(banner => ({
			...banner,
			backgroundColor: color
		}));
		setBannerSizes(newBannerSizes);
	};

	const handleBorderColorChange = (color) => {
		setDefaultBorderColor(color);
		// Update all banners with new border color
		const newBannerSizes = bannerSizes.map(banner => ({
			...banner,
			borderColor: color
		}));
		setBannerSizes(newBannerSizes);
	};

	// Add new state for section collapse
	const [sectionsCollapsed, setSectionsCollapsed] = useState({
		database: false,
		defaultSettings: true,	// Set to true to make it collapsed by default
		fontSettings: false,
		buttonSettings: false,
		shapeSettings: false,
		imageSettings: false
	});

	// Add toggle handler
	const toggleSection = (section) => {
		setSectionsCollapsed(prev => ({
			...prev,
			[section]: !prev[section]
		}));
	};

	// Update the sheet row selection handler
	const handleSheetRowChange = (e) => {
		const newRow = sheetRows.find(r => r.rowId === e.target.value);
		setSelectedRow(newRow);

		// Update all banners' elements with new content from the selected row
		const newBannerSizes = bannerSizes.map(banner => ({
			...banner,
			elements: banner.elements.map(element => {
				// Only update text and button elements that have a cell link
				if ((element.type === 'text' || element.type === 'button' || element.type === 'image') && element.cell) {
					return {
						...element,
						content: newRow.cells[element.cell] || element.content // Fallback to current content if cell not found
					};
				}
				return element;
			})
		}));

		setBannerSizes(newBannerSizes);
	};

	// Add new state for image selection
	const [showImageSelector, setShowImageSelector] = useState(false);

	// Add handler for image cell selection
	const handleImageCellSelect = (cell, value) => {
		if (selectedBannerIndex === null) return;

		const newBannerSizes = [...bannerSizes];
		newBannerSizes[selectedBannerIndex].elements.push({
			id: Date.now(),
			layerId: `image_${cell}`,
			type: 'image',
			content: value, // This will be the image URL
			position: { x: 0, y: 0 },
			size: { width: 100, height: 100 },
			cell: cell,
			style: {
				opacity: 1,
				mixBlendMode: 'normal',
				objectFit: 'contain' // or 'cover', 'fill', etc.
			}
		});
		setBannerSizes(newBannerSizes);
		setShowImageSelector(false);
	};

	// Add the image selector dialog component
	const renderImageSelector = () => {
		if (!selectedRow || selectedBannerIndex === null || !bannerSizes[selectedBannerIndex]) return null;

		// Get all used cells in the current banner
		const usedCells = bannerSizes[selectedBannerIndex].elements
			.map(element => element.cell);

		// Filter cells that contain "Image" or "image"
		const imageCells = Object.entries(selectedRow.cells).filter(([cell, value]) => {
			return (cell.includes('Image') || cell.includes('image')) && 
						 !usedCells.includes(cell);
		});

		return (
			<Dialog 
				open={showImageSelector} 
				onClose={() => setShowImageSelector(false)}
				maxWidth="sm"
				fullWidth
				className="text-selector-dialog"
			>
				<DialogTitle>Select Image from Sheet</DialogTitle>
				<DialogContent>
					<List>
						{imageCells.length > 0 ? (
							imageCells.map(([cell, value]) => (
								<MuiMenuItem 
									key={cell}
									onClick={() => handleImageCellSelect(cell, value)}
								>
									<ListItemText 
										primary={cell}

										primaryTypographyProps={{
											style: {
												fontWeight: 500,
												fontSize: '16px'
											}
										}}
										secondaryTypographyProps={{
											style: {
												fontSize: '13px',
												color: 'rgba(0, 0, 0, 0.6)'
											}
										}}
									/>
									{/* Preview thumbnail if value is a valid URL */}
									{value && value.match(/^https?:\/\/.+/) && (
										<Box sx={{ ml: 2, width: 50, height: 50 }}>
											<img 
												src={value} 
												alt={cell}
												style={{ 
													width: '100%', 
													height: '100%', 
													objectFit: 'contain' 
												}}
											/>
										</Box>
									)}
								</MuiMenuItem>
							))
						) : (
							<Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
								No available image cells found
							</Typography>
						)}
					</List>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setShowImageSelector(false)}>Cancel</Button>
				</DialogActions>
			</Dialog>
		);
	};

	// Add this function to handle gradient type changes
	const handleGradientTypeChange = (type) => {
		if (!selectedElement) return;

		// Get current fill style
		const currentFill = selectedElement.style?.fill || '';
		
		// Extract colors and stops from current gradient
		let colors = [];
		if (currentFill.includes('gradient')) {
			const matches = currentFill.match(/(#[0-9a-f]{6}|rgba?\([^)]+\))\s*(\d+)?%?/gi);
			if (matches) {
				colors = matches.map(match => {
					const [color, stop] = match.split(' ');
					return { color, stop: stop ? parseInt(stop) : null };
				});
			}
		}

		// If no gradient exists yet, create default gradient
		if (colors.length === 0) {
			colors = [
				{ color: '#000000', stop: 0 },
				{ color: '#ffffff', stop: 100 }
			];
		}

		// Construct new gradient string based on type
		let newGradient = '';
		if (type === 'linear') {
			newGradient = `linear-gradient(90deg, ${colors.map(c => 
				`${c.color}${c.stop !== null ? ` ${c.stop}%` : ''}`
			).join(', ')})`;
		} else {
			newGradient = `radial-gradient(${colors.map(c => 
				`${c.color}${c.stop !== null ? ` ${c.stop}%` : ''}`
			).join(', ')})`;
		}

		// Update the element's fill style
		const updatedElement = {
			...selectedElement,
			style: {
				...selectedElement.style,
				fill: newGradient
			}
		};

		// Update the element in the banner
		handleElementUpdate(updatedElement);
	};

	// Add this function near your other element handling functions
	const handleElementUpdate = (updatedElement) => {
		// Check if selectedBannerIndex is null/undefined or out of bounds
		if (selectedBannerIndex === null || selectedBannerIndex === undefined || !bannerSizes[selectedBannerIndex]) {
			console.warn('No banner selected or invalid banner index');
			return;
		}

		const newBannerSizes = [...bannerSizes];
		const banner = newBannerSizes[selectedBannerIndex];

		// Check if banner and elements array exist
		if (!banner || !banner.elements) {
			console.warn('Banner or elements array is undefined');
			return;
		}

		const elementIndex = banner.elements.findIndex(el => el.id === updatedElement.id);
		
		if (elementIndex !== -1) {
			banner.elements[elementIndex] = updatedElement;
			setBannerSizes(newBannerSizes);
			setSelectedElement(updatedElement);
		}
	};

	// Add to state declarations
	const [maskShape, setMaskShape] = useState(null);

	// Add the IMAGE SETTINGS section in the left panel
	<Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.12)' }} />

	{/* IMAGE SETTINGS Section */}
	{selectedElement?.type === 'image' && (
		<>
			<Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.12)' }} />
			<Box className="database-section">
				<Box 
					sx={{ 
						display: 'flex', 
						alignItems: 'center', 
						justifyContent: 'space-between', 
						cursor: 'pointer' 
					}}
					onClick={() => toggleSection('imageSettings')}
				>
					<h2>IMAGE SETTINGS</h2>
					{sectionsCollapsed.imageSettings ? <ExpandMoreIcon /> : <ExpandLessIcon />}
				</Box>
				<Collapse in={!sectionsCollapsed.imageSettings}>
					<Box sx={{ mt: 2 }}>
						<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
							Mask Shape
						</InputLabel>
						<Select
							value={selectedElement.style?.maskId || ''}
							onChange={(e) => handleMaskChange(e.target.value)}
							size="small"
							fullWidth
							sx={{ bgcolor: 'white' }}
						>
							<MenuItem value="">None</MenuItem>
							{bannerSizes[selectedBannerIndex]?.elements
								.filter(el => el.type === 'shape')
								.map(shape => (
									<MenuItem key={shape.id} value={shape.id}>
										{shape.layerId || `Shape ${shape.id}`}
									</MenuItem>
								))
							}
							<MenuItem value="new">+ Create New Shape</MenuItem>
						</Select>

						<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1, mt: 2 }}>
							Object Fit
						</InputLabel>
						<Select
							value={selectedElement.style?.objectFit || 'contain'}
							onChange={(e) => handleObjectFitChange(e.target.value)}
							size="small"
							fullWidth
							sx={{ bgcolor: 'white' }}
						>
							<MenuItem value="contain">Contain</MenuItem>
							<MenuItem value="cover">Cover</MenuItem>
							<MenuItem value="fill">Fill</MenuItem>
						</Select>

						<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1, mt: 2 }}>
							Opacity
						</InputLabel>
						<Slider
							value={selectedElement.style?.opacity || 1}
							onChange={(e, newValue) => handleOpacityChange(newValue)}
							min={0}
							max={1}
							step={0.1}
							valueLabelDisplay="auto"
							sx={{ color: 'white' }}
						/>
					</Box>
				</Collapse>
			</Box>
		</>
	)}

	// Add these handler functions
	const handleObjectFitChange = (value) => {
		if (!selectedElement) return;
		
		const updatedElement = {
			...selectedElement,
			style: {
				...selectedElement.style,
				objectFit: value
			}
		};
		handleElementUpdate(updatedElement);
	};

	const handleOpacityChange = (value) => {
		if (!selectedElement) return;
		
		const updatedElement = {
			...selectedElement,
			style: {
				...selectedElement.style,
				opacity: value
			}
		};
		handleElementUpdate(updatedElement);
	};

	const handleGradientColorChange = (index, color) => {
		const newColors = [...gradientColors];
		newColors[index] = color;
		setGradientColors(newColors);

		const gradientString = fillType === 'linear'
			? `linear-gradient(${gradientAngle}deg, ${newColors[0]} 0%, ${newColors[1]} 100%)`
			: `radial-gradient(circle, ${newColors[0]} 0%, ${newColors[1]} 100%)`;

		const updatedElement = {
			...selectedElement,
			style: {
				...selectedElement.style,
				fill: gradientString
			}
		};
		handleElementUpdate(updatedElement);
	};

	const handleGradientAngleChange = (angle) => {
		setGradientAngle(angle);
		const gradientString = `linear-gradient(${angle}deg, ${gradientColors[0]} 0%, ${gradientColors[1]} 100%)`;
		
		const updatedElement = {
			...selectedElement,
			style: {
				...selectedElement.style,
				fill: gradientString
			}
		};
		handleElementUpdate(updatedElement);
	};

	// Add this function to handle gradient updates
	const updateGradient = (colors, angle) => {
		if (!selectedElement) return;

		const gradientString = fillType === 'linear'
			? `linear-gradient(${angle}deg, ${colors[0]} 0%, ${colors[1]} 100%)`
			: `radial-gradient(circle, ${colors[0]} 0%, ${colors[1]} 100%)`;

		const updatedElement = {
			...selectedElement,
			style: {
				...selectedElement.style,
				fill: gradientString,
				fillType: fillType,
				gradientAngle: angle,
				gradientColors: colors
			}
		};
		handleElementUpdate(updatedElement);
	};

	// Add this function before exportBannerHTML
	const getFontSize = (element) => {
		const domElement = document.getElementById(element.layerId || `layer${element.id}`);
		if (domElement) {
			const txtElement = domElement.querySelector('.txt');
			if (txtElement) {
				return window.getComputedStyle(txtElement).fontSize;
			}
		}
		return '16px'; // fallback
	};

	// Update the gradient handling in ButtonSettings
	const handleGradientChange = (colors, angle) => {
		const gradientString = fillType === 'linear'
			? `linear-gradient(${angle}deg, ${colors[0]} 0%, ${colors[1]} 100%)`
			: `radial-gradient(circle, ${colors[0]} 0%, ${colors[1]} 100%)`;

		handleButtonStyleChange('backgroundColor', gradientString);
	};

	// Add this function near other handler functions
	const handleShapeStyleChange = (property, value) => {
		if (!selectedElement) return;

		const newBannerSizes = [...bannerSizes];
		const elementIndex = newBannerSizes[selectedBannerIndex].elements.findIndex(
			el => el.id === selectedElement.id
		);

		if (elementIndex !== -1) {
			// Create a new style object with the updated property
			const newStyle = {
				...newBannerSizes[selectedBannerIndex].elements[elementIndex].style || {},
				[property]: value
			};

			// Update the element with the new style
			newBannerSizes[selectedBannerIndex].elements[elementIndex] = {
				...newBannerSizes[selectedBannerIndex].elements[elementIndex],
				style: newStyle
			};

			// If we're initializing gradient stops, also set the fillType
			if (property === 'gradientStops' && !newStyle.fillType) {
				newStyle.fillType = 'linear';
			}

			setBannerSizes(newBannerSizes);

			// Update selected element to trigger re-render
			setSelectedElement({
				...newBannerSizes[selectedBannerIndex].elements[elementIndex]
			});
		}
	};

	// Update the getShapeElement function to handle all shapes and stroke styles
	const getShapeElement = (element) => {
		const fill = element.style?.fillType !== 'solid' 
			? `url(#gradient-${element.id})` 
			: element.style?.fill || '#000000';

		// Handle stroke/border styles
		const strokeAttrs = element.style?.stroke ? `
			stroke="${element.style.stroke}"
			stroke-width="${element.style.strokeWidth || 0}"
			${element.style.strokeStyle === 'dashed' ? 'stroke-dasharray="10,5"' : ''}
			${element.style.strokeStyle === 'dotted' ? 'stroke-dasharray="2,2"' : ''}
		` : '';

		switch (element.shapeType) {
			case 'rectangle':
				return `<rect
					width="100%"
					height="100%"
					rx="${element.style?.cornerRadius || 0}"
					ry="${element.style?.cornerRadius || 0}"
					fill="${fill}"
					${strokeAttrs}
					opacity="${element.style?.opacity || 1}"
				/>`;
			
			case 'circle':
				const diameter = Math.min(element.size.width, element.size.height);
				return `<circle
					cx="${element.size.width/2}"
					cy="${element.size.height/2}"
					r="${diameter/2}"
					fill="${fill}"
					${strokeAttrs}
					opacity="${element.style?.opacity || 1}"
				/>`;
			
			case 'triangle':
				const points = `0,${element.size.height} ${element.size.width/2},0 ${element.size.width},${element.size.height}`;
				return `<polygon
					points="${points}"
					fill="${fill}"
					${strokeAttrs}
					opacity="${element.style?.opacity || 1}"
				/>`;

			case 'path':
				return `<path
					d="${element.pathData}"
					fill="${fill}"
					${strokeAttrs}
					opacity="${element.style?.opacity || 1}"
				/>`;

			default:
				return `<rect
					width="100%"
					height="100%"
					fill="${fill}"
					${strokeAttrs}
					opacity="${element.style?.opacity || 1}"
				/>`;
		}
	};

	// Add this function inside your BannerEditor component
	const handleLayerAction = (action, elementId) => {
		if (!selectedBannerForLayers === null || !tempElements) return;

		const updatedElements = [...tempElements];
		const elementIndex = updatedElements.findIndex(el => el.id === elementId);

		switch (action) {
			case 'toggleVisibility':
				if (elementIndex !== -1) {
					const element = updatedElements[elementIndex];
					
					// Initialize style if it doesn't exist
					if (!element.style) {
						element.style = {};
					}

					// Toggle visibility
					const newVisibility = element.style.visibility === 'hidden' ? 'visible' : 'hidden';
					
					// Update element style
					updatedElements[elementIndex] = {
						...element,
						style: {
							...element.style,
							visibility: newVisibility
						}
					};

					// Update banner elements immediately
					const updatedBannerSizes = [...bannerSizes];
					const bannerElementIndex = updatedBannerSizes[selectedBannerForLayers].elements.findIndex(
						el => el.id === elementId
					);

					if (bannerElementIndex !== -1) {
						updatedBannerSizes[selectedBannerForLayers].elements[bannerElementIndex] = {
							...updatedBannerSizes[selectedBannerForLayers].elements[bannerElementIndex],
							style: {
								...updatedBannerSizes[selectedBannerForLayers].elements[bannerElementIndex].style,
								visibility: newVisibility
							}
						};
					}

					setBannerSizes(updatedBannerSizes);
				}
				break;

			case 'duplicate':
				if (elementIndex !== -1) {
					const elementToDuplicate = updatedElements[elementIndex];
					const duplicatedElement = {
						...elementToDuplicate,
						id: `id_${Math.random().toString(36).substr(2, 9)}`,
						layerId: `${elementToDuplicate.layerId}_copy`
					};
					updatedElements.push(duplicatedElement);
				}
				break;
		}

		setTempElements(updatedElements);
	};

	// Make sure your SVG elements have data-id attributes for direct DOM manipulation
	const renderSVGElement = (element) => {
		return (
			<div
				id={`element-${element.id}`}	// Add this unique ID
				className="svg-element"
				style={{
					position: 'absolute',
					left: element.position.x,
					top: element.position.y,
					width: element.size.width,
					height: element.size.height,
					visibility: element.style?.visibility || 'visible',
					...element.style
				}}
			>
				<SVGElement
					element={element}
					style={element.style}
				/>
			</div>
		);
	};

	// Then add this useEffect to initialize banner names when bannerSizes changes
	useEffect(() => {
		setBannerNames(bannerSizes.map((_, index) => `Banner ${index + 1}`));
	}, [bannerSizes]);

	// Add this useEffect to handle banner name initialization
	useEffect(() => {
		// Update banner names while preserving existing names
		const updatedBannerNames = { ...bannerNames };
		bannerSizes.forEach((size, index) => {
			const dimensionKey = `${size.width}x${size.height}`;
			if (!updatedBannerNames[dimensionKey]) {
				updatedBannerNames[dimensionKey] = `Banner`;
			}
		});
		setBannerNames(updatedBannerNames);
	}, [bannerSizes]);

	// Add this function to handle custom URL confirmation
	const handleConfirmCustomUrl = () => {
		if (customScriptUrl.trim()) {
			setIsCustomUrlConfirmed(true);
		}
	};

	// Add these state variables where your other states are defined
	const [customScripts, setCustomScripts] = useState([{ url: '', isConfirmed: false }]);

	// Add these functions to handle custom scripts
	const handleCustomScriptChange = (index, value) => {
		const newScripts = [...customScripts];
		newScripts[index] = { ...newScripts[index], url: value, isConfirmed: false };
		setCustomScripts(newScripts);
	};

	const handleConfirmScript = (index) => {
		const newScripts = [...customScripts];
		newScripts[index] = { ...newScripts[index], isConfirmed: true };
		setCustomScripts(newScripts);
	};

	const handleAddScript = () => {
		setCustomScripts([...customScripts, { url: '', isConfirmed: false }]);
	};

	const handleRemoveScript = (index) => {
		const newScripts = customScripts.filter((_, i) => i !== index);
		setCustomScripts(newScripts);
	};

	// Add this helper function to format the date
	const formatDate = () => {
		const now = new Date();
		const options = { 
			timeZone: 'Europe/Budapest',
			year: 'numeric',
			month: 'short',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit'
		};
		return now.toLocaleString('en-US', options).replace(',', '') + ' CET';
	};

	// Add this handler with your other handlers
	const handleClickTagChange = (event) => {
		const value = event.target.value;
		
		// If 'no CT' is being selected, clear all other selections
		if (!selectedClickTags.includes('no CT') && value.includes('no CT')) {
			setSelectedClickTags(['no CT']);
			return;
		}

		// If any other option is being selected while 'no CT' is selected,
		// remove 'no CT' and add the new selection
		if (selectedClickTags.includes('no CT')) {
			setSelectedClickTags(value.filter(tag => tag !== 'no CT'));
			return;
		}

		setSelectedClickTags(value);
	};

	// Add this helper function to get banner name
	const getBannerFileName = (banner, clickTag = null) => {
		const baseName = banner.name || 'Banner';
		const dimensions = `${banner.width}x${banner.height}`;
		
		if (!clickTag || clickTag === 'no CT') {
			return `${dimensions}_${baseName}.html`;
		}
		
		const cleanClickTagName = clickTag.replace(/\s+/g, '');
		return `${dimensions}_${baseName}_${cleanClickTagName}.html`;
	};

	// FADER generation
	const generateAnimationFaderCss = (banner) => {
		if (selectedAnimationLibrary !== 'GSAP') return '';
		else return `#FADER{position:absolute;width:${banner.width}px;height:${banner.height}px;top:0;left:0;background-color: ${defaultBackgroundColor};}`;
	};

	// Add this function in your component
	const generateAnimationScript = (banner, animationSettings) => {
		if (!animationSettings.enabled) return '';
		
		return `
		<!-- BANNER ANIMATION JS -->
		<script type="text/javascript">
			/* beautify ignore:start */
			var WIDTH=${banner.width},HEIGHT=${banner.height};
			
			/*** BANNER ***/
			var _SPEED=${animationSettings.speed},_INTERVAL=_SPEED*${animationSettings.interval},_DELAY=_SPEED*${animationSettings.delay},_PAUSE=_SPEED*${animationSettings.pause},TOTALDURATION=0,timeScale=${animationSettings.speed},dyn=Power3,OUT=dyn.easeOut,IN=dyn.easeIn,INOUT=dyn.easeInOut,LIN=Power0.easeNone,BACK=Back.easeOut.config(${animationSettings.back}),BACKINOUT=Back.easeInOut.config(${animationSettings.backInout}),BOUNCE=Bounce.easeOut,SINE=Sine.easeInOut,min=0.01,runCount = 4;

			/*** GSAP ***/
			gsap.defaults({
				ease: 'power3.inOut',
				stagger: .085,
				duration: ${animationSettings.speed},
				transformOrigin: "50% 50%",
			});

			var BANNER = gsap.timeline({
				onComplete: reStart,
				paused: true
			});
			BANNER.timeScale( timeScale );
			
			/*** TIMELINE ***/
			window.onload = function () {
				
				console.log('Starting BANNER.')
				
				BANNER
				//
				.addLabel('start')
					.to(FADER, _SPEED, { alpha:0 }, 'start' )

				.addLabel('p1', '-='+_DELAY)

				;

				BANNER.play('start');
			}

			/*** RESTART ***/
			function reStart() {
				if ((--runCount)>0){
					BANNER.pause();
					
					TOTALDURATION += BANNER.totalDuration();
					console.log('Banner Duration: '+TOTALDURATION.toFixed(1));
					//
					var endSequence = new TimelineMax();
					endSequence.timeScale( timeScale );
					endSequence
					.addLabel('fader', '+='+_PAUSE)
					.to(FADER, 0.35, { opacity:1, onComplete: function(){ 
						TOTALDURATION+=endSequence.totalDuration()
						BANNER.restart()
					}}, 'fader' );
					
				} else{
					TOTALDURATION += BANNER.totalDuration();
					console.log("Total Duration: "+TOTALDURATION.toFixed(1));
				}
			}
			/*** BANNER ***/
			
			/* beautify ignore:end */
		</script>`;
	};
	
	return (
		<Box className="banner-editor-container">
			<Drawer
				variant="permanent"
				sx={{
					width: DRAWER_WIDTH,
					flexShrink: 0,
					'& .MuiDrawer-paper': {
						width: DRAWER_WIDTH,
						boxSizing: 'border-box',
					},
				}}
			>
				<Toolbar />
				
							{/* Font Settings Section - Only show when text element is selected */}
							{selectedElement?.type === 'text' && (
								<Box className="database-section">
									<Box 
										sx={{ 
											display: 'flex', 
											alignItems: 'center', 
											justifyContent: 'space-between', 
											cursor: 'pointer' 
										}}
										onClick={() => toggleSection('fontSettings')}
									>
										<h2>FONT SETTINGS</h2>
										{sectionsCollapsed.fontSettings ? <ExpandMoreIcon /> : <ExpandLessIcon />}
									</Box>
									<Collapse in={!sectionsCollapsed.fontSettings}>
										<Box sx={{ mt: 2 }}>
											{/* Font Family Dropdown */}
											<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
												Font Family
											</InputLabel>
											<Select
												value={selectedElement.style?.fontFamily || 'Roboto'}
												onChange={(e) => handleFontChange(e.target.value)}
												size="small"
												fullWidth
												sx={{ bgcolor: 'white', mb: 2 }}
											>
												{googleFonts.map((font) => (
													<MenuItem key={font} value={font} sx={{ fontFamily: font }}>
														{font}
													</MenuItem>
												))}
											</Select>

											<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
												Max Font Size
											</InputLabel>
											<FormControl fullWidth sx={{ bgcolor: 'white', mb: 1 }}>
												<Select
													size="small"
													value={selectedElement.maxFontSize || 16}
													onChange={(e) => {
														handleElementUpdate({
															...selectedElement,
															maxFontSize: e.target.value
														});
													}}
												>
													{maxFontSizes.map((size) => (
														<MenuItem key={size} value={size}>
															{size}px
														</MenuItem>
													))}
												</Select>
											</FormControl>

											<InputLabel sx={{ color: 'white', fontSize: '12px', mt: 1 }}>
												Current Font Size
											</InputLabel>
											<TextField
												fullWidth
												value={(() => {
													const textElement = document.querySelector(`#${selectedElement.layerId} .txt`);
													return textElement ? `${parseInt(window.getComputedStyle(textElement).fontSize)}px` : '0px';
												})()}
												InputProps={{
													readOnly: true,
												}}
												variant="outlined"
												size="small"
												sx={{ mt: 1 }}
											/>

											{/* Color Picker */}
											<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1, mt:2 }}>
												Text Color
											</InputLabel>
											<input
												type="color"
												size="small"
												value={selectedElement.style?.color || '#000000'}
												onChange={(e) => {
													const updatedElement = {
														...selectedElement,
														style: {
															...selectedElement.style,
															color: e.target.value
														}
													};
													handleElementUpdate(updatedElement);
												}}
												style={{ 
													width: '100%', 
													height: '40px', 
													marginBottom: '16px',
													padding: 0,
													border: 'none'
												}}
											/>

											{/* Text Case Controls */}
											<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
												Text Case
											</InputLabel>
											<Select
												value={selectedElement.style?.textTransform || 'none'}
												size="small"
												onChange={(e) => {
													const updatedElement = {
														...selectedElement,
														style: {
															...selectedElement.style,
															textTransform: e.target.value
														}
													};
													handleElementUpdate(updatedElement);
												}}
												fullWidth
												size="small"
												sx={{ bgcolor: 'white', mb: 2 }}
											>
												<MenuItem value="none">Normal</MenuItem>
												<MenuItem value="uppercase">ALL CAPS</MenuItem>
												<MenuItem value="lowercase">small caps</MenuItem>
											</Select>

											{/* Font Style Controls */}
											<Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
												<IconButton
													onClick={() => {
														const updatedElement = {
															...selectedElement,
															style: {
																...selectedElement.style,
																fontWeight: selectedElement.style?.fontWeight === 'bold' ? 'normal' : 'bold'
															}
														};
														handleElementUpdate(updatedElement);
													}}
													sx={{ 
														flex: 1,
														color: selectedElement.style?.fontWeight === 'bold' ? 'var(--orange)' : 'white',
														border: `1px solid ${selectedElement.style?.fontWeight === 'bold' ? 'var(--orange)' : 'rgba(255,255,255,0.23)'}`,
														padding: '8px'
													}}
												>
													<FormatBoldIcon />
												</IconButton>
												<IconButton
													onClick={() => {
														const updatedElement = {
															...selectedElement,
															style: {
																...selectedElement.style,
																fontStyle: selectedElement.style?.fontStyle === 'italic' ? 'normal' : 'italic'
															}
														};
														handleElementUpdate(updatedElement);
													}}
													sx={{ 
														flex: 1,
														color: selectedElement.style?.fontStyle === 'italic' ? 'var(--orange)' : 'white',
														border: `1px solid ${selectedElement.style?.fontStyle === 'italic' ? 'var(--orange)' : 'rgba(255,255,255,0.23)'}`,
														padding: '8px'
													}}
												>
													<FormatItalicIcon />
												</IconButton>
											</Box>

											<Box sx={{ mb: 2 }}>
												<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
													Text Alignment
												</InputLabel>
												<Box sx={{ display: 'flex', gap: 1 }}>
													<IconButton 
														onClick={() => handleElementUpdate({
															...selectedElement,
															style: {
																...selectedElement.style,
																textAlign: 'left',
																justifyContent: 'flex-start'
															}
														})}
														sx={{ 
															bgcolor: selectedElement.style?.textAlign === 'left' ? 'rgba(238, 75, 15, 0.1)' : 'transparent',
															color: selectedElement.style?.textAlign === 'left' ? 'var(--orange)' : 'inherit'
														}}
													>
														<FormatAlignLeftIcon />
													</IconButton>
													
													<IconButton 
														onClick={() => handleElementUpdate({
															...selectedElement,
															style: {
																...selectedElement.style,
																textAlign: 'center',
																justifyContent: 'center'
															}
														})}
														sx={{ 
															bgcolor: selectedElement.style?.textAlign === 'center' ? 'rgba(238, 75, 15, 0.1)' : 'transparent',
															color: selectedElement.style?.textAlign === 'center' ? 'var(--orange)' : 'inherit'
														}}
													>
														<FormatAlignCenterIcon />
													</IconButton>
													
													<IconButton 
														onClick={() => handleElementUpdate({
															...selectedElement,
															style: {
																...selectedElement.style,
																textAlign: 'right',
																justifyContent: 'flex-end'
															}
														})}
														sx={{ 
															bgcolor: selectedElement.style?.textAlign === 'right' ? 'rgba(238, 75, 15, 0.1)' : 'transparent',
															color: selectedElement.style?.textAlign === 'right' ? 'var(--orange)' : 'inherit'
														}}
													>
														<FormatAlignRightIcon />
													</IconButton>
												</Box>
											</Box>

											{/* Line Height Control */}
											<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
												Line Height
											</InputLabel>
											<Slider
												value={parseFloat(selectedElement.style?.lineHeight) || 1}
												onChange={(e, newValue) => {
													const updatedElement = {
														...selectedElement,
														style: {
															...selectedElement.style,
															lineHeight: newValue.toString()
														}
													};
													handleElementUpdate(updatedElement);
												}}
												min={0.5}
												max={3}
												step={0.1}
												valueLabelDisplay="auto"
												sx={{ color: 'white' }}
											/>
										</Box>
									</Collapse>
								</Box>
							)}

							{/* Button Settings Section - Only show when button element is selected */}
							{selectedElement?.type === 'button' && (
								<Box className="database-section">
									<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
											 onClick={() => toggleSection('buttonSettings')}>
										<h2>BUTTON SETTINGS</h2>
										{sectionsCollapsed.buttonSettings ? <ExpandMoreIcon /> : <ExpandLessIcon />}
									</Box>
									<Collapse in={!sectionsCollapsed.buttonSettings}>
										<Box sx={{ mt: 2 }}>
											<ButtonSettings />
										</Box>
									</Collapse>
								</Box>
							)}

							{/* Add IMAGE SETTINGS Section here */}
							{selectedElement?.type === 'image' && (
								<Box className="database-section">
									<Box 
										sx={{ 
											display: 'flex', 
											alignItems: 'center', 
											justifyContent: 'space-between', 
											cursor: 'pointer',
											mb: 2
										}}
										onClick={() => toggleSection('imageSettings')}
									>
										<h2>IMAGE SETTINGS</h2>
										{sectionsCollapsed.imageSettings ? <ExpandMoreIcon /> : <ExpandLessIcon />}
									</Box>

									<Collapse in={!sectionsCollapsed.imageSettings}>
										<Box sx={{ mt: 2 }}>
											<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
												Mask Shape
											</InputLabel>
											<Select
												value={selectedElement.style?.maskId || ''}
												onChange={(e) => handleMaskChange(e.target.value)}
												size="small"
												fullWidth
												sx={{ bgcolor: 'white', mb: 2 }}
											>
												<MenuItem value="">None</MenuItem>
												{bannerSizes[selectedBannerIndex]?.elements
													.filter(el => el.type === 'shape')
													.map(shape => (
														<MenuItem key={shape.id} value={shape.id}>
															{shape.layerId || `Shape ${shape.id}`}
														</MenuItem>
													))
												}
												<MenuItem value="new">+ Create New Shape</MenuItem>
											</Select>

											<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
												Object Fit
											</InputLabel>
											<Select
												value={selectedElement.style?.objectFit || 'contain'}
												onChange={(e) => handleObjectFitChange(e.target.value)}
												size="small"
												fullWidth
												sx={{ bgcolor: 'white', mb: 2 }}
											>
												<MenuItem value="contain">Contain</MenuItem>
												<MenuItem value="cover">Cover</MenuItem>
												<MenuItem value="fill">Fill</MenuItem>
											</Select>

											<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
												Opacity
											</InputLabel>
											<Slider
												value={selectedElement.style?.opacity || 1}
												onChange={(e, newValue) => handleOpacityChange(newValue)}
												min={0}
												max={1}
												step={0.1}
												valueLabelDisplay="auto"
												sx={{ color: 'white' }}
											/>
										</Box>
									</Collapse>
								</Box>
							)}

							{/* Shape Style Section - Only show when shape element is selected */}
							{selectedElement?.type === 'shape' && (
								<Box className="database-section">
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											cursor: 'pointer',
										}}
										onClick={() => toggleSection('shapeSettings')}
									>
										<h2>SHAPE SETTINGS</h2>
										{sectionsCollapsed.shapeSettings ? <ExpandMoreIcon /> : <ExpandLessIcon />}
									</Box>
									<Collapse in={!sectionsCollapsed.shapeSettings}>
										<Box sx={{ mt: 2 }}>
											{/* Fill Type Selector */}
											<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>Fill Type</InputLabel>
											<Select
												value={selectedElement.style?.fillType || 'solid'}
												onChange={(e) => {
													const newFillType = e.target.value;
													// Initialize gradient stops when switching to gradient
													if ((newFillType === 'linear' || newFillType === 'radial') && !selectedElement.style?.gradientStops) {
														handleShapeStyleChange('gradientStops', [
															{ color: '#000000', offset: 0, opacity: 1 },
															{ color: '#ffffff', offset: 100, opacity: 1 },
														]);
													}
													handleShapeStyleChange('fillType', newFillType);
												}}
												size="small"
												fullWidth
												sx={{ bgcolor: 'white', mb: 1 }}
											>
												<MenuItem value="solid">Solid Color</MenuItem>
												<MenuItem value="linear">Linear Gradient</MenuItem>
												<MenuItem value="radial">Radial Gradient</MenuItem>
											</Select>

											{/* Solid Color Picker */}
											{(selectedElement.style?.fillType || 'solid') === 'solid' && (
												<Box sx={{ mt: 1 }}>
													<input
														type="color"
														value={selectedElement.style?.fill || '#000000'}
														onChange={(e) => {
															const newColor = e.target.value;
															handleShapeStyleChange('fill', newColor);
															handleShapeStyleChange('fillColor', newColor);
														}}
														style={{ width: '100%', height: '40px' }}
													/>
												</Box>
											)}

											{/* Gradient Controls */}
											{(selectedElement.style?.fillType === 'linear' || selectedElement.style?.fillType === 'radial') && (
												<Box sx={{ mt: 1 }}>
													{(selectedElement.style?.gradientStops || [
														{ color: '#000000', offset: 0, opacity: 1 },
														{ color: '#ffffff', offset: 100, opacity: 1 },
													]).map((stop, index) => (
														<Box
															key={index}
															sx={{
																mb: 1.5,
																display: 'flex',
																alignItems: 'center',
																gap: 1,
															}}
														>
															<input
																type="color"
																value={stop.color}
																onChange={(e) => {
																	const newStops = [...(selectedElement.style?.gradientStops || [])];
																	newStops[index] = { ...stop, color: e.target.value };
																	handleShapeStyleChange('gradientStops', newStops);
																}}
																style={{ width: '40px', height: '40px' }}
															/>

															<Box sx={{ flex: 1 }}>
																<Box sx={{ mb: 1 }}>
																	<Typography sx={{ color: 'white', fontSize: '12px', mb: 0.5 }}>Location</Typography>
																	<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
																		<Slider
																			size="small"
																			value={stop.offset}
																			onChange={(e, newValue) => {
																				const newStops = [...(selectedElement.style?.gradientStops || [])];
																				newStops[index] = { ...stop, offset: newValue };
																				handleShapeStyleChange('gradientStops', newStops);
																			}}
																			min={0}
																			max={100}
																			sx={{ flex: 1, color: 'white' }}
																		/>
																		<TextField
																			size="small"
																			value={stop.offset}
																			onChange={(e) => {
																				const newValue = Math.min(100, Math.max(0, Number(e.target.value)));
																				const newStops = [...(selectedElement.style?.gradientStops || [])];
																				newStops[index] = { ...stop, offset: newValue };
																				handleShapeStyleChange('gradientStops', newStops);
																			}}
																			sx={{ width: '60px' }}
																			type="number"
																		/>
																	</Box>
																</Box>

																<Box>
																	<Typography sx={{ color: 'white', fontSize: '12px', mb: 0.5 }}>Opacity</Typography>
																	<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
																		<Slider
																			size="small"
																			value={stop.opacity}
																			onChange={(e, newValue) => {
																				const newStops = [...(selectedElement.style?.gradientStops || [])];
																				newStops[index] = { ...stop, opacity: newValue };
																				handleShapeStyleChange('gradientStops', newStops);
																			}}
																			min={0}
																			max={1}
																			step={0.01}
																			sx={{ flex: 1, color: 'white' }}
																		/>
																		<TextField
																			size="small"
																			value={Math.round(stop.opacity * 100)}
																			onChange={(e) => {
																				const newValue = Math.min(100, Math.max(0, Number(e.target.value))) / 100;
																				const newStops = [...(selectedElement.style?.gradientStops || [])];
																				newStops[index] = { ...stop, opacity: newValue };
																				handleShapeStyleChange('gradientStops', newStops);
																			}}
																			sx={{ width: '60px' }}
																			type="number"
																		/>
																	</Box>
																</Box>
															</Box>

															{index > 1 && (
																<IconButton
																	size="small"
																	onClick={() => {
																		const newStops = selectedElement.style?.gradientStops.filter((_, i) => i !== index);
																		handleShapeStyleChange('gradientStops', newStops);
																	}}
																	sx={{ color: 'white' }}
																>
																	<DeleteIcon />
																</IconButton>
															)}
														</Box>
													))}

													<Box
														sx={{
															display: 'flex',
															alignItems: 'center',
															gap: 1,
															mt: 2,
															color: 'white',
														}}
													>
														<IconButton
															size="small"
															onClick={() => {
																const currentStops = selectedElement.style?.gradientStops || [];
																const newStop = {
																	color: '#808080',
																	offset: 50,
																	opacity: 1,
																};
																handleShapeStyleChange('gradientStops', [...currentStops, newStop]);
															}}
															sx={{
																bgcolor: 'rgba(255,255,255,0.1)',
																color: 'white',
																'&:hover': {
																	bgcolor: 'rgba(255,255,255,0.2)',
																},
															}}
														>
															<AddIcon fontSize="small" />
														</IconButton>
														<Typography sx={{ fontSize: '14px' }}>Add color stop</Typography>
													</Box>

													{selectedElement.style?.fillType === 'linear' && (
														<Box sx={{ mt: 2 }}>
															<Typography sx={{ color: 'white', fontSize: '12px', mb: 0.5 }}>
																Angle: {selectedElement.style?.gradientAngle || 0}°
															</Typography>
															<Slider
																value={selectedElement.style?.gradientAngle || 0}
																onChange={(e, newValue) => handleShapeStyleChange('gradientAngle', newValue)}
																min={0}
																max={360}
																sx={{ color: 'white' }}
															/>
														</Box>
													)}
												</Box>
											)}

											{/* Border Settings */}
											<Box sx={{ mb: 2 }}>
												<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1, mt: 2 }}>Border color</InputLabel>
												<TextField
													fullWidth
													type="color"
													size="small"
													value={selectedElement.style?.stroke || '#000000'}
													onChange={(e) => handleShapeStyleChange('stroke', e.target.value)}
													sx={{ mb: 1 }}
												/>

												<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1, mt: 2 }}>Border Width</InputLabel>
												<TextField
													fullWidth
													type="number"
													size="small"
													value={selectedElement.style?.strokeWidth || 0}
													onChange={(e) => handleShapeStyleChange('strokeWidth', parseInt(e.target.value))}
													sx={{ mb: 1 }}
												/>

												<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1, mt: 2 }}>Border Style</InputLabel>
												<FormControl fullWidth>
													<Select
														size="small"
														value={selectedElement.style?.strokeStyle || 'solid'}
														onChange={(e) => handleShapeStyleChange('strokeStyle', e.target.value)}
														sx={{ bgcolor: 'white' }}
													>
														<MenuItem value="solid">Solid</MenuItem>
														<MenuItem value="dashed">Dashed</MenuItem>
														<MenuItem value="dotted">Dotted</MenuItem>
													</Select>
												</FormControl>
											</Box>

											{/* Corner Radius */}
											{selectedElement.shapeType === 'rectangle' && (
												<Box sx={{ mb: 2 }}>
													<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1, mt: 2 }}>Corner Radius</InputLabel>
													<TextField
														size="small"
														fullWidth
														type="number"
														value={selectedElement.style?.cornerRadius || 0}
														onChange={(e) => handleShapeStyleChange('cornerRadius', parseInt(e.target.value))}
													/>
												</Box>
											)}

											{/* Blend Mode */}
											<Box sx={{ mb: 2 }}>
												<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1, mt: 2 }}>Blend Mode</InputLabel>
												<FormControl fullWidth>
													<Select
														size="small"
														value={selectedElement.style?.mixBlendMode || 'normal'}
														onChange={(e) => handleShapeStyleChange('mixBlendMode', e.target.value)}
														sx={{ bgcolor: 'white' }}
													>
														<MenuItem value="normal">Normal</MenuItem>
														<MenuItem value="multiply">Multiply</MenuItem>
														<MenuItem value="screen">Screen</MenuItem>
														<MenuItem value="overlay">Overlay</MenuItem>
														<MenuItem value="darken">Darken</MenuItem>
														<MenuItem value="lighten">Lighten</MenuItem>
														<MenuItem value="color-dodge">Color Dodge</MenuItem>
														<MenuItem value="color-burn">Color Burn</MenuItem>
														<MenuItem value="hard-light">Hard Light</MenuItem>
														<MenuItem value="soft-light">Soft Light</MenuItem>
														<MenuItem value="difference">Difference</MenuItem>
														<MenuItem value="exclusion">Exclusion</MenuItem>
													</Select>
												</FormControl>
											</Box>

											{/* Opacity Slider */}
											<Box sx={{ mb: 2 }}>
												<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1, mt: 2 }}>Opacity</InputLabel>
												<Slider
													value={selectedElement.style?.opacity || 1}
													onChange={(e, newValue) => handleShapeStyleChange({
														...selectedElement,
														style: { ...selectedElement.style, opacity: newValue },
													})}
													color='white'
													min={0}
													max={1}
													step={0.01}
													valueLabelDisplay="auto"
												/>
											</Box>
										</Box>
									</Collapse>
								</Box>
							)}


							{/* BANNER SIZES Section */}
							<Box className="database-section">
								<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
										 onClick={() => toggleSection('bannerSizes')}>
									<h2>BANNER SIZES</h2>
									{sectionsCollapsed.bannerSizes ? <ExpandMoreIcon /> : <ExpandLessIcon />}
								</Box>
								<Collapse in={!sectionsCollapsed.bannerSizes}>
									<Box sx={{ mt: 2 }}>
										<Box className="banner-sizes">
											{customSizes.map((size, index) => (
												<div key={index} className="size-display">
													{size.width}x{size.height}
													<div 
														className="delete-size"
														onClick={() => handleDeleteClick(index)}
													>
														<CloseIcon sx={{ fontSize: 12 }} />
													</div>
												</div>
											))}
											
											{showSizeInput ? (
												<TextField
													size="small"
													value={newSize}
													onChange={(e) => setNewSize(e.target.value)}
													placeholder="e.g., 300x600"
													onKeyPress={(e) => {
														if (e.key === 'Enter') {
															handleAddSize();
														}
													}}
													InputProps={{
														endAdornment: (
															<InputAdornment position="end">
																<IconButton size="small" onClick={handleAddSize}>
																	<AddCircleIcon sx={{ color: 'white' }} />
																</IconButton>
															</InputAdornment>
														),
													}}
													sx={{ 
														'& .MuiOutlinedInput-root': {
															'& fieldset': {
																borderColor: 'rgba(255,255,255,0.23)',
															},
														},
													}}
												/>
											) : (
												<IconButton onClick={handleAddSize}>
													<AddCircleIcon sx={{ color: 'white' }} />
												</IconButton>
											)}
										</Box>
									</Box>
								</Collapse>
							</Box>

							<Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.12)' }} />

							{/* BANNER SETTINGS Section */}
							<Box className="database-section">
								<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
										 onClick={() => toggleSection('defaultSettings')}>
									<h2>BANNER SETTINGS</h2>
									{sectionsCollapsed.defaultSettings ? <ExpandMoreIcon /> : <ExpandLessIcon />}
								</Box>
								<Collapse in={!sectionsCollapsed.defaultSettings}>
									<Box sx={{ mt: 2 }}>
										<Box sx={{ mb: 2 }}>
											<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
												Background Color
											</InputLabel>
											<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
												<input
													type="color"
													value={defaultBackgroundColor}
													onChange={(e) => handleBackgroundColorChange(e.target.value)}
													style={{ width: '40px', height: '40px' }}
												/>
												<Typography variant="body2" sx={{ color: 'white' }}>
													{defaultBackgroundColor}
												</Typography>
											</Box>
										</Box>

										<Box sx={{ mb: 2 }}>
											<InputLabel sx={{ color: 'white', fontSize: '12px', mb: 1 }}>
												Border Color
											</InputLabel>
											<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
												<input
													type="color"
													value={defaultBorderColor}
													onChange={(e) => handleBorderColorChange(e.target.value)}
													style={{ width: '40px', height: '40px' }}
												/>
												<Typography variant="body2" sx={{ color: 'white' }}>
													{defaultBorderColor}
												</Typography>
											</Box>
										</Box>
									</Box>

									{/* ClickTag Settings */}
									<Box sx={{ mb: 2 }}>
										<Box 
											sx={{ 
												display: 'flex', 
												alignItems: 'center', 
												justifyContent: 'space-between',
												cursor: 'pointer',
												mb: 1 
											}}
											onClick={() => setClickTagsCollapsed(!clickTagsCollapsed)}
										>
											<InputLabel sx={{ color: 'white', fontSize: '12px', cursor: 'pointer' }}>
												ClickTag Settings
											</InputLabel>
											{clickTagsCollapsed ? <ExpandMoreIcon sx={{ color: 'white' }} /> : <ExpandLessIcon sx={{ color: 'white' }} />}
										</Box>

										<Collapse in={!clickTagsCollapsed}>
											<Box sx={{ position: 'relative', zIndex: 1301 }}>
												<Select
													multiple
													value={selectedClickTags}
													onChange={handleClickTagChange}
													size="small"
													input={<OutlinedInput />}
													renderValue={(selected) => selected.join(', ')}
													MenuProps={{
														container: document.body,
														PaperProps: {
															sx: {
																maxHeight: 300,
																backgroundColor: '#424242',
																color: '#333',
																position: 'fixed',
																zIndex: 9999
															}
														}
													}}
													sx={{
														width: '100%',
														color: 'white',
														'.MuiOutlinedInput-notchedOutline': {
															borderColor: 'rgba(255, 255, 255, 0.23)',
														},
														'&:hover .MuiOutlinedInput-notchedOutline': {
															borderColor: 'rgba(255, 255, 255, 0.5)',
														},
														'.MuiSvgIcon-root': {
															color: 'white',
														}
													}}
												>
													<MenuItem value="no CT">
														<Checkbox checked={selectedClickTags.includes('no CT')} />
														<ListItemText primary="None" />
													</MenuItem>
													<MenuItem value="Google Ads">
														<Checkbox checked={selectedClickTags.includes('Google Ads')} />
														<ListItemText primary="Google Ads" />
													</MenuItem>
													<MenuItem value="Google DC360">
														<Checkbox checked={selectedClickTags.includes('Google DC360')} />
														<ListItemText primary="Google DC360" />
													</MenuItem>
													<MenuItem value="AdForm">
														<Checkbox checked={selectedClickTags.includes('AdForm')} />
														<ListItemText primary="AdForm" />
													</MenuItem>
													<MenuItem value="Gemius">
														<Checkbox checked={selectedClickTags.includes('Gemius')} />
														<ListItemText primary="Gemius" />
													</MenuItem>
													<MenuItem value="Adverticum">
														<Checkbox checked={selectedClickTags.includes('Adverticum')} />
														<ListItemText primary="Adverticum" />
													</MenuItem>
												</Select>
											</Box>
										</Collapse>
									</Box>

									{/* Include Scripts */}
									<Box sx={{ mb: 2 }}>
										<Box 
											sx={{ 
												display: 'flex', 
												alignItems: 'center', 
												justifyContent: 'space-between',
												cursor: 'pointer',
												mb: 1 
											}}
											onClick={() => setIncludeScriptsCollapsed(!includeScriptsCollapsed)}
										>
											<InputLabel sx={{ color: 'white', fontSize: '12px', cursor: 'pointer' }}>
												Include Scripts
											</InputLabel>
											{includeScriptsCollapsed ? <ExpandMoreIcon sx={{ color: 'white' }} /> : <ExpandLessIcon sx={{ color: 'white' }} />}
										</Box>

										<Collapse in={!includeScriptsCollapsed}>
											<Box sx={{ position: 'relative', zIndex: 1301 }}>
												<Select
													multiple
													value={selectedLibraries}
													onChange={handleLibraryChange}
													size="small"
													input={<OutlinedInput />}
													renderValue={(selected) => selected.join(', ')}
													sx={{
														width: '100%',
														color: 'white',
														'.MuiOutlinedInput-notchedOutline': {
															borderColor: 'rgba(255, 255, 255, 0.23)',
														},
														'&:hover .MuiOutlinedInput-notchedOutline': {
															borderColor: 'rgba(255, 255, 255, 0.5)',
														},
														'.MuiSvgIcon-root': {
															color: 'white',
														}
													}}
												>
													<MenuItem value="none">
														<Checkbox checked={selectedLibraries.includes('none')} />
														<ListItemText primary="None" />
													</MenuItem>
													<MenuItem value="GSAP">
														<Checkbox checked={selectedLibraries.includes('GSAP')} />
														<ListItemText primary="GSAP" />
													</MenuItem>
													<MenuItem value="jQuery">
														<Checkbox checked={selectedLibraries.includes('jQuery')} />
														<ListItemText primary="jQuery" />
													</MenuItem>
													<MenuItem value="Custom">
														<Checkbox checked={selectedLibraries.includes('Custom')} />
														<ListItemText primary="Custom" />
													</MenuItem>
												</Select>
											</Box>

											{/* Multiple Custom Script URL Inputs */}
											{selectedLibraries.includes('Custom') && (
												<Box sx={{ mt: 1 }}>
													{customScripts.map((script, index) => (
														<Box 
															key={index} 
															sx={{ 
																display: 'flex', 
																gap: 1, 
																mb: 1,
																alignItems: 'center'
															}}
														>
															<TextField
																fullWidth
																placeholder="Enter custom script URL"
																value={script.url}
																size="small"
																onChange={(e) => handleCustomScriptChange(index, e.target.value)}
																disabled={script.isConfirmed}
																sx={{
																	bgcolor: 'white',
																	'& .MuiOutlinedInput-root': {
																	height: '40px',
																	'& input': {
																		color: '#333',
																	},
																	'& fieldset': {
																		borderColor: '#ddd',
																	},
																	'&:hover fieldset': {
																		borderColor: '#999',
																	},
																	},
																	'&.Mui-disabled': {
																	bgcolor: '#f5f5f5',
																	'& .MuiOutlinedInput-root': {
																		'& input': {
																		color: '#666',
																		},
																	},
																	},
																}}
																/>
															<IconButton 
																onClick={() => handleConfirmScript(index)}
																disabled={!script.url.trim() || script.isConfirmed}
																sx={{ 
																	color: script.isConfirmed ? 'var(--orange)' : 'white',
																	'&.Mui-disabled': {
																		color: 'rgba(255, 255, 255, 0.3)',
																	}
																}}
															>
																<LinkIcon />
															</IconButton>
															<IconButton 
																onClick={() => handleRemoveScript(index)}
																sx={{ 
																	color: 'white',
																	'&:hover': {
																		color: '#ff4444'
																	}
																}}
															>
																<DeleteIcon />
															</IconButton>
														</Box>
													))}
													
													{/* Add New Script Button */}
													<Button
														startIcon={<AddIcon />}
														onClick={handleAddScript}
														sx={{
															color: 'white',
															borderColor: 'rgba(255, 255, 255, 0.23)',
															'&:hover': {
																borderColor: 'rgba(255, 255, 255, 0.5)',
															},
															mt: 1
														}}
														variant="outlined"
														size="small"
														fullWidth
													>
														Add Another Script
													</Button>
												</Box>
											)}

										</Collapse>
									</Box>

									{/* Animation Settings */}
									<Box sx={{ mb: 2 }}>
										<Box 
											sx={{ 
												display: 'flex', 
												alignItems: 'center', 
												justifyContent: 'space-between',
												cursor: 'pointer',
												mb: 1 
											}}
											onClick={() => setAnimationSettingsCollapsed(!animationSettingsCollapsed)}
										>
											<InputLabel sx={{ color: 'white', fontSize: '12px', cursor: 'pointer' }}>
												Animation Settings
											</InputLabel>
											{animationSettingsCollapsed ? <ExpandMoreIcon sx={{ color: 'white' }} /> : <ExpandLessIcon sx={{ color: 'white' }} />}
										</Box>

										<Collapse in={!animationSettingsCollapsed && selectedLibraries.length > 0 && !selectedLibraries.includes('none')}>
											<Box sx={{ position: 'relative', zIndex: 1301 }}>
																			<FormControl fullWidth>
																				{/* Conditionally render the InputLabel */}
													{!selectedAnimationLibrary && (
														<InputLabel
															sx={{
																color: 'white',
																fontSize: '12px',
																'&.Mui-focused': {
																	color: 'white',
																},
															}}
														>
															Animate With:
														</InputLabel>
													)}
													<Select
														value={selectedAnimationLibrary}
														onChange={(e) => setSelectedAnimationLibrary(e.target.value)}
														size="small"
														MenuProps={{
															container: document.body,
															PaperProps: {
																sx: {
																	maxHeight: 300,
																	backgroundColor: '#424242',
																	color: '#333',
																	position: 'fixed',
																	zIndex: 9999,
																},
															},
														}}
														sx={{
															width: '100%',
															color: 'white',
															'.MuiOutlinedInput-notchedOutline': {
																borderColor: 'rgba(255, 255, 255, 0.23)',
															},
															'&:hover .MuiOutlinedInput-notchedOutline': {
																borderColor: 'rgba(255, 255, 255, 0.5)',
															},
															'.MuiSvgIcon-root': {
																color: 'white',
															},
														}}
													>
														{selectedLibraries.length === 0 || (selectedLibraries.length === 1 && selectedLibraries[0] === 'none') ? (
															<MenuItem disabled>Add library first!</MenuItem>
														) : (
															selectedLibraries
																.filter((lib) => lib !== 'none')
																.map((library) => (
																	<MenuItem key={library} value={library}>
																		{library}
																	</MenuItem>
																))
														)}
													</Select>
												</FormControl>
											</Box>
											
											{selectedAnimationLibrary === 'GSAP' && (
											<>
												{/* Speed Dropdown */}
												<Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
													<Typography sx={{ mr: 1, color: 'white', fontSize: '12px' }}>
														SPEED:
													</Typography>
													<FormControl sx={{ flex: 1 }}>
														<Select
															size="small"
															value={animationSpeed}
															onChange={(e) => setAnimationSpeed(e.target.value)}
															displayEmpty
															sx={{
																color: 'white',
																'.MuiOutlinedInput-notchedOutline': {
																	borderColor: 'rgba(255, 255, 255, 0.23)',
																},
																'&:hover .MuiOutlinedInput-notchedOutline': {
																	borderColor: 'rgba(255, 255, 255, 0.5)',
																},
																'.MuiSvgIcon-root': {
																	color: 'white',
																}
															}}
														>
															<MenuItem value="" disabled>
																<em>SPEED</em>
															</MenuItem>
															{['0.75', '1.00', '1.25', '1.5', '1.75'].map((speed) => (
																<MenuItem key={speed} value={speed}>{speed}</MenuItem>
															))}
														</Select>
													</FormControl>
													<Typography sx={{ ml: 1, color: 'white', fontSize: '12px' }}>
														* SPEED
													</Typography>
												</Box>

												{/* Interval Dropdown */}
												<Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
													<Typography sx={{ mr: 1, color: 'white', fontSize: '12px' }}>
														INTERVAL:
													</Typography>
													<FormControl sx={{ flex: 1 }}>
														<Select
															size="small"
															value={animationInterval}
															onChange={(e) => setAnimationInterval(e.target.value)}
															displayEmpty
															sx={{
																color: 'white',
																'.MuiOutlinedInput-notchedOutline': {
																	borderColor: 'rgba(255, 255, 255, 0.23)',
																},
																'&:hover .MuiOutlinedInput-notchedOutline': {
																	borderColor: 'rgba(255, 255, 255, 0.5)',
																},
																'.MuiSvgIcon-root': {
																	color: 'white',
																}
															}}
														>
															<MenuItem value="" disabled>
																<em>INTERVAL</em>
															</MenuItem>
															{['0.2', '0.3', '0.5'].map((interval) => (
																<MenuItem key={interval} value={interval}>{interval}</MenuItem>
															))}
														</Select>
													</FormControl>
													<Typography sx={{ ml: 1, color: 'white', fontSize: '12px' }}>
														* SPEED
													</Typography>
												</Box>

												{/* Delay Dropdown */}
												<Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
													<Typography sx={{ mr: 1, color: 'white', fontSize: '12px' }}>
														DELAY:
													</Typography>
													<FormControl sx={{ flex: 1 }}>
														<Select
															size="small"
															value={animationDelay}
															onChange={(e) => setAnimationDelay(e.target.value)}
															displayEmpty
															sx={{
																color: 'white',
																'.MuiOutlinedInput-notchedOutline': {
																	borderColor: 'rgba(255, 255, 255, 0.23)',
																},
																'&:hover .MuiOutlinedInput-notchedOutline': {
																	borderColor: 'rgba(255, 255, 255, 0.5)',
																},
																'.MuiSvgIcon-root': {
																	color: 'white',
																}
															}}
														>
															<MenuItem value="" disabled>
																<em>DELAY</em>
															</MenuItem>
															{['0.65', '0.85'].map((delay) => (
																<MenuItem key={delay} value={delay}>{delay}</MenuItem>
															))}
														</Select>
													</FormControl>
													<Typography sx={{ ml: 1, color: 'white', fontSize: '12px' }}>
														* SPEED
													</Typography>
												</Box>

												{/* Pause Dropdown */}
												<Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
													<Typography sx={{ mr: 1, color: 'white', fontSize: '12px' }}>
														PAUSE:
													</Typography>
													<FormControl sx={{ flex: 1 }}>
														<Select
															size="small"
															value={animationPause}
															onChange={(e) => setAnimationPause(e.target.value)}
															displayEmpty
															sx={{
																color: 'white',
																'.MuiOutlinedInput-notchedOutline': {
																	borderColor: 'rgba(255, 255, 255, 0.23)',
																},
																'&:hover .MuiOutlinedInput-notchedOutline': {
																	borderColor: 'rgba(255, 255, 255, 0.5)',
																},
																'.MuiSvgIcon-root': {
																	color: 'white',
																}
															}}
														>
															<MenuItem value="" disabled>
																<em>PAUSE</em>
															</MenuItem>
															{['2', '2.5', '3'].map((pause) => (
																<MenuItem key={pause} value={pause}>{pause}</MenuItem>
															))}
														</Select>
													</FormControl>
													<Typography sx={{ ml: 1, color: 'white', fontSize: '12px' }}>
														* SPEED
													</Typography>
												</Box>

												{/* Ease Back Dropdown */}
												<Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
													<Typography sx={{ mr: 1, color: 'white', fontSize: '12px' }}>
														EaseBACK:
													</Typography>
													<FormControl sx={{ flex: 1 }}>
														<Select
															size="small"
															value={animationBack}
															onChange={(e) => setAnimationBack(e.target.value)}
															displayEmpty
															sx={{
																color: 'white',
																'.MuiOutlinedInput-notchedOutline': {
																	borderColor: 'rgba(255, 255, 255, 0.23)',
																},
																'&:hover .MuiOutlinedInput-notchedOutline': {
																	borderColor: 'rgba(255, 255, 255, 0.5)',
																},
																'.MuiSvgIcon-root': {
																	color: 'white',
																}
															}}
														>
															<MenuItem value="" disabled>
																<em>EaseBACK</em>
															</MenuItem>
															{['0.7', '1.00', '1.25', '1.5', '1.75'].map((speed) => (
																<MenuItem key={speed} value={speed}>{speed}</MenuItem>
															))}
														</Select>
													</FormControl>
												</Box>

												{/* Ease BackInout Dropdown */}
												<Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
													<Typography sx={{ mr: 1, color: 'white', fontSize: '12px' }}>
														EaseBACKINOUT:
													</Typography>
													<FormControl sx={{ flex: 1 }}>
														<Select
															size="small"
															value={animationBackInout}
															onChange={(e) => setAnimationBackInout(e.target.value)}
															displayEmpty
															sx={{
																color: 'white',
																'.MuiOutlinedInput-notchedOutline': {
																	borderColor: 'rgba(255, 255, 255, 0.23)',
																},
																'&:hover .MuiOutlinedInput-notchedOutline': {
																	borderColor: 'rgba(255, 255, 255, 0.5)',
																},
																'.MuiSvgIcon-root': {
																	color: 'white',
																}
															}}
														>
															<MenuItem value="" disabled>
																<em>EaseBACKINOUT</em>
															</MenuItem>
															{['0.7', '1.00', '1.25', '1.5', '1.75'].map((speed) => (
																<MenuItem key={speed} value={speed}>{speed}</MenuItem>
															))}
														</Select>
													</FormControl>
												</Box>

												{/* TimeScale Dropdown */}
												<Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
													<Typography sx={{ mr: 1, color: 'white', fontSize: '12px' }}>
														TimeScale:
													</Typography>
													<FormControl sx={{ flex: 1 }}>
														<Select
															size="small"
															value={animationTimeScale}
															onChange={(e) => setAnimationTimeScale(e.target.value)}
															displayEmpty
															sx={{
																color: 'white',
																'.MuiOutlinedInput-notchedOutline': {
																	borderColor: 'rgba(255, 255, 255, 0.23)',
																},
																'&:hover .MuiOutlinedInput-notchedOutline': {
																	borderColor: 'rgba(255, 255, 255, 0.5)',
																},
																'.MuiSvgIcon-root': {
																	color: 'white',
																}
															}}
														>
															<MenuItem value="" disabled>
																<em>TimeScale</em>
															</MenuItem>
															{['0.7', '1.00', '1.25', '1.5', '1.75'].map((speed) => (
																<MenuItem key={speed} value={speed}>{speed}</MenuItem>
															))}
														</Select>
													</FormControl>
												</Box>
											</>
										)}

									</Collapse>
								</Box>
								</Collapse>	
							</Box>

							<Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.12)' }} />
				
				{/* DATABASE Section */}
				<Box className="database-section">
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} 
							 onClick={() => toggleSection('database')}>
						<h2>DATABASE SETTINGS</h2>
						{sectionsCollapsed.database ? <ExpandMoreIcon /> : <ExpandLessIcon />}
					</Box>
					<Collapse in={!sectionsCollapsed.database}>
						{/* Database settings content */}
						<Box sx={{ mt: 2 }}>							
							<InputLabel 
								htmlFor="sheet-id" 
								sx={{ 
									color: 'white', 
									fontSize: '12px',
									marginBottom: '8px' 
								}}
							>
								Sheet ID
							</InputLabel>
							<div className="sheet-id-container">
								<TextField
									id="sheet-id"
									placeholder="Enter Sheet ID"
									size="small"
									value={sheetUrl}
									onChange={(e) => setSheetUrl(e.target.value)}
								/>
								<Button 
									className="connect-button"
									onClick={handleSheetConnect}
								>
									<LinkIcon />
								</Button>
							</div>

							{sheets.length > 1 && (
								<Box mt={2}>
									<TextField
										select
										fullWidth
										size="small"
										placeholder="Select Sheet"
										value={selectedSheet}
										onChange={(e) => setSelectedSheet(e.target.value)}
									>
										{sheets.map((sheet) => (
											<MenuItem key={sheet.id} value={sheet.id}>
												{sheet.name}
											</MenuItem>
										))}
									</TextField>
								</Box>
							)}

							{sheetRows.length > 0 && (
								<Box mt={2}>
									<InputLabel 
										htmlFor="sheet-row" 
										sx={{ 
											color: 'white', 
											fontSize: '12px',
											marginBottom: '8px' 
										}}
									>
										Sheet Row
									</InputLabel>
									<TextField
										id="sheet-row"
										select
										fullWidth
										size="small"
										value={selectedRow?.rowId || ''}
										onChange={handleSheetRowChange}	// Use the new handler here
										sx={{ mt: 1 }}
									>
										{sheetRows.map((row) => (
											<MenuItem key={row.rowId} value={row.rowId}>
												Row {row.rowId}
											</MenuItem>
										))}
									</TextField>
								</Box>
							)}

						</Box>
					</Collapse>
				</Box>

				<Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.12)' }} />

			</Drawer>

			<Box className="banner-scroll-container">
				{/* UI Controls Layer - Moved to top level */}
				<div className="controls-layer">
					{bannerSizes.map((size, bannerIndex) => 
						size.elements.map((element) => (
							<Draggable
								key={`control_${element.id}`}
								position={{
									x: element.position.x + bannerIndex * (size.width + 60) + 60,
									y: element.position.y + 100
								}}
								onDrag={(e, data) => {
									const adjustedX = data.x - (bannerIndex * (size.width + 60) + 60);
									const adjustedY = data.y - 100;
									handleDrag(bannerIndex, element.id, e, {
										x: adjustedX,
										y: adjustedY
									});
								}}
								handle=".drag-handle"
								onMouseDown={(e) => {
									handleElementClick(bannerIndex, element.id, e);
									e.stopPropagation();
								}}
							>
								<div 
									className={`element-controls ${selectedElement?.id === element.id ? 'selected' : ''} ${selectedElements.includes(element.id) ? 'multi-selected' : ''}`}
									style={{
										position: 'absolute',
										width: element.size.width,
										height: element.size.height,
										pointerEvents: 'auto',
										zIndex: 2
									}}
								>
									{/* Transparent drag handle */}
									<div className="drag-handle" style={{ width: '100%', height: '100%' }} />

									{/* Delete button */}
									<div 
										className="element-delete"
										onClick={(e) => {
											e.stopPropagation();
											handleDeleteElement(element.id);
										}}
									>
										<CloseIcon sx={{ fontSize: 12 }} />
									</div>

									{/* Add Aspect Ratio Toggle for images */}
									{element.type === 'image' && (
										<div 
											className="aspect-ratio-toggle"
											onClick={(e) => {
												e.stopPropagation();
												const keepAspectRatio = element.style?.keepAspectRatio !== false;
												const updatedElement = {
													...element,
													style: {
														...element.style,
														keepAspectRatio: !keepAspectRatio
													}
												};
												handleElementUpdate(updatedElement);
											}}
										>
											{element.style?.keepAspectRatio !== false ? <LinkIcon /> : <LinkOffIcon />}
										</div>
									)}

									{/* Resize handles */}
									{selectedElement?.id === element.id && (
										<>
											<div className="resize-handle resize-handle-n" onMouseDown={(e) => handleResizeStart(e, 'n')} />
											<div className="resize-handle resize-handle-s" onMouseDown={(e) => handleResizeStart(e, 's')} />
											<div className="resize-handle resize-handle-e" onMouseDown={(e) => handleResizeStart(e, 'e')} />
											<div className="resize-handle resize-handle-w" onMouseDown={(e) => handleResizeStart(e, 'w')} />
											<div className="resize-handle resize-handle-nw" onMouseDown={(e) => handleResizeStart(e, 'nw')} />
											<div className="resize-handle resize-handle-ne" onMouseDown={(e) => handleResizeStart(e, 'ne')} />
											<div className="resize-handle resize-handle-sw" onMouseDown={(e) => handleResizeStart(e, 'sw')} />
											<div className="resize-handle resize-handle-se" onMouseDown={(e) => handleResizeStart(e, 'se')} />
										</>
									)}
								</div>
							</Draggable>
						))
					)}
				</div>

				{/* Banner Grid */}
				<Box className="banner-grid">
					{bannerSizes.map((size, index) => (
						<Box key={index} className="banner-item">
							<div className="banner-header">
								<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
									{editingBannerIndex === index ? (
										<TextField
											autoFocus
											defaultValue={bannerNames[`${size.width}x${size.height}`]}
											onBlur={(e) => {
												const dimensionKey = `${size.width}x${size.height}`;
												setBannerNames(prev => ({
													...prev,
													[dimensionKey]: e.target.value || `Banner_${dimensionKey}`
												}));
												setEditingBannerIndex(null);
											}}
											onKeyPress={(e) => {
												if (e.key === 'Enter') {
													const dimensionKey = `${size.width}x${size.height}`;
													setBannerNames(prev => ({
														...prev,
														[dimensionKey]: e.target.value || `Banner_${dimensionKey}`
													}));
													setEditingBannerIndex(null);
												}
											}}
											size="small"
											sx={{
												'& .MuiInputBase-input': {
													color: 'white',
													fontSize: '14px',
													padding: '2px 8px',
												},
												'& .MuiOutlinedInput-root': {
													'& fieldset': {
														borderColor: 'rgba(255, 255, 255, 0.23)',
													},
													'&:hover fieldset': {
														borderColor: 'rgba(255, 255, 255, 0.5)',
													},
												},
											}}
										/>
									) : (
										<div 
											onClick={() => setEditingBannerIndex(index)}
											style={{ cursor: 'pointer' }}
										>
											{bannerNames[`${size.width}x${size.height}`] || `Banner_${size.width}x${size.height}`}
										</div>
									)}
									<div>{size.width}x{size.height}</div>
								</div>
								<Box sx={{ display: 'flex', gap: 1 }}>
									<IconButton 
										size="small" 
										color="inherit"
										onClick={() => handleLayerClick(index)}
									>
										<LayersIcon />
									</IconButton>
									<IconButton 
										size="small" 
										color="inherit"
										onClick={(e) => handleAddElementClick(e, index)}
									>
										<AddIcon />
									</IconButton>
								</Box>
							</div>
							<div className="banner-content">
								<div 
									className="banner-preview"
									key={size._updateKey}
									style={{
										width: size.width,
										height: size.height,
										maxWidth: '100%',
										cursor: isDrawingMode ? 'crosshair' : 'default',
										position: 'relative',
										overflow: 'hidden',
										border: `1px solid ${size.borderColor || defaultBorderColor}`,
										backgroundColor: size.backgroundColor || defaultBackgroundColor,
										isolation: 'isolate',
										transform: 'translateZ(0)'
									}}
									onClick={handleBannerClick}
									onMouseDown={(e) => handleBannerMouseDown(e, index)}
									onMouseMove={(e) => handleBannerMouseMove(e, index)}
									onMouseUp={handleBannerMouseUp}
									onDoubleClick={() => handleBannerDoubleClick(index)}
								>
									{size.elements.map((element) => (
										<div 
											key={`content_${element.id}`}
											id={element.layerId}
											className="banner-element"
											style={{
												position: 'absolute',
												left: element.position.x,
												top: element.position.y,
												width: element.size.width,
												height: element.size.height,
												mixBlendMode: element.style?.mixBlendMode || 'normal',
												visibility: element.style?.visibility || 'visible'	// Add this line
											}}
										>
											{element.type === 'text' && (() => {
												// Get the actual rendered font size from the element's style
												const txtElement = document.querySelector(`#${element.layerId} .txt`);
												const computedStyle = txtElement ? window.getComputedStyle(txtElement) : null;
												const actualFontSize = computedStyle?.fontSize || '16px';

												return (
													<div className="txt_container" style={{
														visibility: element.style?.visibility || 'visible'	// Add this line
													}}>
														<div 
															className="txt"
															style={{
																fontFamily: element.style?.fontFamily || 'Roboto',
																fontSize: actualFontSize,	// Use the actual computed font size
																textAlign: element.style?.textAlign || 'center',
																color: element.style?.color || '#000000',
																textTransform: element.style?.textTransform || 'none',
																fontWeight: element.style?.fontWeight || 'normal',
																fontStyle: element.style?.fontStyle || 'normal',
																lineHeight: element.style?.lineHeight || '1',
																width: '100%',
																height: '100%',
																display: 'flex',
																alignItems: 'center',
																justifyContent: 'center',
															}}
														>
															{element.content}
														</div>
													</div>
												);
											})()}

											{element.type === 'button' && (() => {
												const btnElement = document.querySelector(`#${element.layerId} .txt`);
												const computedStyle = btnElement ? window.getComputedStyle(btnElement) : null;
												const actualFontSize = computedStyle?.fontSize || '16px';

												return (
													<div className="txt_container" style={{
														width: '100%',
														height: '100%',
														background: element.style?.backgroundColor || '#ffffff',
														borderRadius: `${element.style?.borderRadius || 0}px`,
														border: `${element.style?.borderWidth || 0}px ${element.style?.borderStyle || 'solid'} ${element.style?.borderColor || '#000000'}`,
														visibility: element.style?.visibility || 'visible'	// Add this line
													}}>
														<div 
															className="txt"
															style={{
																width: '100%',
																height: '100%',
																color: element.style?.color || '#000000',
																fontSize: actualFontSize,
																fontFamily: element.style?.fontFamily || 'Roboto',
																fontWeight: element.style?.fontWeight || 'normal',
																fontStyle: element.style?.fontStyle || 'normal',
																padding: `${element.style?.padding?.top || 0}px ${element.style?.padding?.right || 0}px ${element.style?.padding?.bottom || 0}px ${element.style?.padding?.left || 0}px`,
																display: 'flex',
																alignItems: element.style?.padding?.top === element.style?.padding?.bottom ? 'center' : 'flex-start',
																justifyContent: element.style?.textAlign === 'left' ? 'flex-start' : 
																							 element.style?.textAlign === 'right' ? 'flex-end' : 'center', // Add this line
																textAlign: element.style?.textAlign || 'left', // Add this line
																lineHeight: '1',
																whiteSpace: 'nowrap',
																overflow: 'hidden',
																textOverflow: 'ellipsis',
																boxSizing: 'border-box'
															}}
														>
															{element.content}
														</div>
													</div>
												);
											})()}

											{element.type === 'shape' && (
												<SVGElement
													element={element}
													style={{
														width: '100%',
														height: '100%',
														fill: element.style?.fill || '#000000',
														stroke: element.style?.stroke || 'none',
														strokeWidth: element.style?.strokeWidth || 0,
														opacity: element.style?.opacity || 1,
														transform: `rotate(${element.style?.rotation || 0}deg)`,
														mixBlendMode: element.style?.mixBlendMode || 'normal',
														visibility: element.style?.visibility || 'visible'	// Add this line
													}}
													bannerSizes={bannerSizes}
													selectedBannerIndex={selectedBannerIndex}
												/>
											)}

											{element.type === 'image' && (() => {
												// Find the shape being used as mask
												const maskShape = element.style?.maskId ? 
													bannerSizes[selectedBannerIndex].elements.find(el => 
														el.id === element.style.maskId
													) : null;

												// Get the layerId from the shape
												const maskShapeLayerId = maskShape?.layerId;

												return (
													<img 
														src={element.content} 
														alt={element.layerId || ''}
														style={{
															width: `${element.size.width}px`,	// Use exact width
															height: `${element.size.height}px`, // Use exact height
															objectFit: element.style?.objectFit || 'contain',
															opacity: element.style?.opacity || 1,
															mixBlendMode: element.style?.mixBlendMode || 'normal',
															mask: maskShapeLayerId ? `url(#${maskShapeLayerId}Mask)` : 'none',
															WebkitMask: maskShapeLayerId ? `url(#${maskShapeLayerId}Mask)` : 'none',
															visibility: element.style?.visibility || 'visible'	// Add this line
														}}
													/>
												);
											})()}
										</div>
									))}
								</div>
							</div>

							<div className="banner-controls">
								<FullscreenIcon className="control-button" />
								<ImageIcon className="control-button" />
								<RefreshIcon className="control-button" />
								<EditIcon className="control-button" />
								<PreviewIcon className="control-button" />
								<DownloadOutlinedIcon 
									className="control-button" 
									onClick={() => {
										const currentBanner = bannerSizes[index]; // Use the banner's own index
										if (currentBanner) {
											handleDownloadBanner(currentBanner, index);
										} else {
											console.error('No banner selected');
										}
									}}
									style={{ cursor: 'pointer' }}
								/>
							</div>
						</Box>
					))}
				</Box>
			</Box>

			<button className="download-button">
				Download Banners
				<DownloadIcon />
			</button>

			<Dialog
				open={deleteConfirmOpen}
				onClose={() => setDeleteConfirmOpen(false)}
			>
				<DialogTitle>Delete Banner Size</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Are you sure you want to delete this banner size? This will remove the banner and all its contents.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
					<Button onClick={handleDeleteConfirm} color="error">Delete</Button>
				</DialogActions>
			</Dialog>

			{/* Element Type Menu */}
			<Menu
				anchorEl={elementMenu}
				open={Boolean(elementMenu)}
				onClose={() => setElementMenu(null)}
			>
				<MenuList>
					<MuiMenuItem onClick={() => handleElementTypeSelect('text')}>
						<ListItemText 
							primary="Add Text Field"
							secondary="Add text from sheet"
						/>
					</MuiMenuItem>
					<MuiMenuItem onClick={() => handleElementTypeSelect('image')}>
						<ListItemText 
							primary="Add Image"
							secondary="Upload or select image"
						/>
					</MuiMenuItem>
					<MuiMenuItem onClick={() => handleElementTypeSelect('button')}>
						<ListItemText 
							primary="Add Button"
							secondary="Add clickable button"
						/>
					</MuiMenuItem>
					<MuiMenuItem onClick={() => handleElementTypeSelect('shape')}>
						<ListItemText 
							primary="Add Shape"
							secondary="Add shape element"
						/>
					</MuiMenuItem>
				</MenuList>
			</Menu>

			{/* Text Selector Dialog */}
			{renderTextSelector()}

			{/* Button Cell Selector Dialog */}
			{renderButtonCellSelector()}

			<AlignmentTools />

			{/* Add this before the closing Box tag */}
			{renderLayersPanel()}

			{/* Shape Selector Dialog */}
			<ShapeSelector 
				open={showShapeSelector}
				onClose={() => setShowShapeSelector(false)}
				onSelect={handleShapeSelect}
			/>

			{/* Image Selector Dialog */}
			{renderImageSelector()}
		</Box>
	);
};

export default BannerEditor; 